import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AppAuthJWTToken, AppAuthService} from '@app/auth';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {backoffice_caisse} from '../../shared/lefrueguanemploy';

export interface InventoryZoneLayout {
	id: number;
	name: string;
	sort_order: number;
	product_ids: number[];
}

export interface InventoryZoneSavePayload {
	id?: number | null;
	name: string;
	product_ids: number[];
}

export interface ProduitCategoryLayout {
	id: number;
	name: string;
	sort_order: number;
	product_ids: number[];
}

export interface ProduitCategorySavePayload {
	id?: number | null;
	name: string;
	product_ids: number[];
}

export interface Frigo {
	id: number;
	restaurant_id: number;
	name: string;
	alert_max_temp: number;
	alert_min_temp: number;
	alert_email: string;
}

export interface AtelierEquipementListItem {
	id: number;
	restaurant_id: number;
	catalog_id?: number | null;
	name: string;
	marque?: string | null;
	type: string;
	ref_fournisseur?: string;
	numero_serie?: string;
	identity_from_catalog?: boolean;
	catalog_name?: string | null;
	fournisseur_nom?: string;
	fournisseur_email?: string;
	fournisseur_phone?: string;
	date_achat?: string;
	montant_achat?: number | null;
	date_fin_garantie?: string;
	has_facture: boolean;
	documentation_count: number;
	warranty_status: 'ok' | 'soon' | 'expired' | 'unknown';
	rebus_at?: string | null;
	rebus_reason?: string | null;
	is_rebus?: boolean;
}

export type AtelierEquipementDocKind = 'SHOP_LINK' | 'MANUAL' | 'PROCEDURE' | 'OTHER';

export interface AtelierEquipementCatalogListItem {
	id: number;
	category_id?: number | null;
	category_code?: string;
	category_label?: string;
	name: string;
	marque?: string | null;
	type: string;
	ref_fournisseur?: string;
	numero_serie?: string;
	fournisseur_nom?: string;
	fournisseur_email?: string;
	fournisseur_phone?: string;
	montant_achat?: number | null;
	notes?: string;
	suivi_equipement?: boolean | number;
	allow_local_add?: boolean | number;
	sort_order?: number;
	shop_url?: string;
	documentation_count: number;
	created_at?: string;
	updated_at?: string;
}

export interface MffCatalogShopLink {
	label: string;
	url: string | null;
	boutique_product_id?: number | null;
}

export interface MffCatalogItem {
	id: number;
	name: string;
	ref_fournisseur?: string;
	fournisseur_nom?: string;
	notes?: string;
	shop_url?: string;
	shop_links?: MffCatalogShopLink[];
	suivi_equipement: boolean;
	allow_local_add?: boolean;
	type: string;
}

export interface MffCatalogGroup {
	id: number | null;
	code: string;
	label: string;
	sort_order: number;
	items: MffCatalogItem[];
}

export interface AtelierEquipementCatalogCategory {
	id: number;
	code: string;
	label: string;
	sort_order: number;
}

export interface AtelierEquipementCatalogDocumentation {
	id: number;
	catalog_id: number;
	label: string;
	doc_kind: AtelierEquipementDocKind;
	source_type: 'UPLOAD' | 'URL';
	source_url?: string;
	boutique_product_id?: number | null;
	file_name?: string;
	downloaded_at?: string;
	created_at?: string;
}

export interface AtelierEquipementCatalogDetail extends AtelierEquipementCatalogListItem {
	documentations: AtelierEquipementCatalogDocumentation[];
	troubleshooting?: AtelierEquipementTroubleshootingEntry[];
	entretien_plan_templates?: AtelierEquipementEntretienPlan[];
}

export interface AtelierEquipementTroubleshootingEntry {
	id: number;
	catalog_id?: number | null;
	equipement_type?: string | null;
	title: string;
	symptoms?: string | null;
	steps: string;
	sort_order?: number;
}

export const ATELIER_EQUIPEMENT_DOC_KINDS = [
	{ value: 'SHOP_LINK', label: 'Lien shop' },
	{ value: 'MANUAL', label: 'Manuel' },
	{ value: 'PROCEDURE', label: 'Procédure' },
	{ value: 'OTHER', label: 'Autre' },
] as const;

export interface AtelierEquipementDocumentation {
	id: number;
	equipement_id: number;
	label: string;
	source_type: 'UPLOAD' | 'URL';
	source_url?: string;
	file_name?: string;
	downloaded_at?: string;
	created_at?: string;
}

export interface AtelierEquipementEntretien {
	id: number;
	equipement_id: number;
	user_id: number;
	date_intervention: string;
	nature: string;
	intervenant?: string;
	montant?: number;
	documents?: { id: number; entretien_id: number; file_name: string }[];
}

export interface AtelierEquipementDetail extends AtelierEquipementListItem {
	notes?: string;
	facture?: { id: number; file_name: string };
	documentations: AtelierEquipementDocumentation[];
	catalog?: AtelierEquipementCatalogListItem;
	catalog_documentations?: AtelierEquipementCatalogDocumentation[];
	entretiens: AtelierEquipementEntretien[];
	sav_history: { id: number; nature_panne: string; destinataire_email: string; sent_at: string }[];
	troubleshooting?: AtelierEquipementTroubleshootingEntry[];
	entretien_plans?: AtelierEquipementEntretienPlan[];
}

export interface AtelierEquipementEntretienPlan {
	id: number;
	equipement_id: number;
	template_id?: number | null;
	label: string;
	frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
	instructions?: string | null;
	moment: 'ouverture' | 'fermeture';
	service_scope: 'first' | 'last' | 'both';
	weekday?: number | null;
	day_of_month?: number | null;
	biweekly_anchor?: string | null;
	sort_order?: number;
	active?: boolean | number;
	equipement_name?: string;
}

export interface AtelierEquipementEntretienDueItem {
	plan_id: number;
	equipement_id: number;
	equipement_name: string;
	label: string;
	service: 'midi' | 'soir';
	moment: 'ouverture' | 'fermeture';
	due_date: string;
	instructions?: string;
	completed: boolean;
}

export const ATELIER_EQUIPEMENT_PLAN_FREQUENCIES = [
	{ value: 'DAILY', label: 'Quotidien' },
	{ value: 'WEEKLY', label: 'Hebdomadaire' },
	{ value: 'BIWEEKLY', label: 'Bi-mensuel' },
	{ value: 'MONTHLY', label: 'Mensuel' },
] as const;

export const ATELIER_EQUIPEMENT_PLAN_MOMENTS = [
	{ value: 'ouverture', label: 'Avant service' },
	{ value: 'fermeture', label: 'Post service' },
] as const;

export const ATELIER_EQUIPEMENT_PLAN_SERVICE_SCOPES = [
	{ value: 'both', label: 'Tous les services' },
	{ value: 'first', label: 'Premier service' },
	{ value: 'last', label: 'Dernier service' },
] as const;

export const ATELIER_EQUIPEMENT_TYPES = [
	{ value: 'FRIGO', label: 'Frigo' },
	{ value: 'MACHINE_GLACE', label: 'Machine à glaçons' },
	{ value: 'LAVE_VERRES', label: 'Lave-verres' },
	{ value: 'RAPEUSE_FROMAGE', label: 'Râpeuse à fromage' },
	{ value: 'MACHINE_CAFE', label: 'Machine à café PRO' },
	{ value: 'FOUR_AIRFRYER', label: 'Four / Airfryer' },
	{ value: 'TRANCHEUSE', label: 'Trancheuse' },
	{ value: 'AUTRE', label: 'Autre' },
];

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	public apiUrl = environment.apiUrl + '/';
	private httpOptions = {
		headers: new HttpHeaders({
			'Content-Type': 'application/json',
		}),
	};
	private token: string = null;

	constructor(private http: HttpClient, private authService: AppAuthService) {
		this.authService.onTokenChange()
			.subscribe((token: AppAuthJWTToken) => {
				if (token.isValid() && token.getPayload().data) {
					this.token = token.getValue();
				} else {
					this.token = null;
				}
			});
	}

	handleError = (error: HttpErrorResponse) => {
		if (error.error instanceof ErrorEvent) {
			console.error('An error occurred:', error.error.message);
		} else if (error.status === 401) {
			// La reconnexion mobile et la redirection sont gérées par AuthHttpInterceptor.
		} else {
			console.error(
				`Backend returned code ${error.status}, ` +
				`body was: ${JSON.stringify(error.error)}`);
		}
		return throwError(() => error);
	};

	getUsers(): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'user', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getLastMonthReservations(restaurantId) {
		return this.http.get<any>(this.apiUrl + 'reservation?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getZeltyReservations(restaurantId, date) {
		return this.http.get<any>(this.apiUrl + 'reservation/zelty?restaurant_id=' + restaurantId + '&date=' + date,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	pushReservationToCaisse(id: any) {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/caisse',
			'',
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createResa(data: any) {
		return this.http.post<any>(this.apiUrl + 'reservation/internal-create',
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	reminderResa(id: any) {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/relance',
			'',
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateReservationNotationEmailSending(id: any, status: any) {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/notation_sending',
			{'after_mail_sendable': status},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateReservationStatus(id: any, status: any, cancelReason: string) {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/status',
			{'status': status, 'cancel_reason': cancelReason},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getReservation(id: any, restaurantId) {
		return this.http.get<any>(this.apiUrl + 'reservation/' + id + '?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateReservation(id: any, data: any) {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/update',
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	notifyReservationClient(id: any) {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/notify-update',
			{},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	sendMessageToReservation(id: any, message: any) {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/message',
			{'message': message},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getRestaurants(): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'restaurants', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createRestaurant(payload: Record<string, unknown>): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurants', payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	uploadRestaurantSiteMedia(restaurantId: number, field: string, file: File): Observable<any> {
		const formData = new FormData();
		formData.append('field', field);
		formData.append('file', file);
		return this.http.post<any>(
			this.apiUrl + 'restaurants/' + restaurantId + '/site-media',
			formData,
			this.getMultipartHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateRestaurant(restaurantId: number, payload: Record<string, unknown>): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurants/' + restaurantId, payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getCommande(commande, restaurantId): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'commande?id=' + commande + '&restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getCommandeTracabilite(commande, restaurantId): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'commande_tracabilite?id=' + commande + '&restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getCommandeTracabiliteProductPhotos(
		commandeId: number,
		restaurantId: number,
		catalogProductId: number,
		lineId: number,
	): Observable<Array<{ commande_id: number; filename: string; from_previous?: boolean }>> {
		return this.http.get<Array<{ commande_id: number; filename: string; from_previous?: boolean }>>(
			this.apiUrl
				+ 'commande_tracabilite?id='
				+ commandeId
				+ '&restaurant_id='
				+ restaurantId
				+ '&product_id='
				+ catalogProductId
				+ '&line_id='
				+ lineId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getCommandeLocalTracabilite(commande, restaurantId): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'commande_local/' + commande + '?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getCommandeTracabiliteImage(commande, image, restaurantId): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'commande_tracabilite?id=' + commande + '&restaurant_id=' + restaurantId
			+ '&file=' + image, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getCommandeLocalTracabiliteImage(commande, image, restaurantId): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'commande_local/' + commande + '?restaurant_id=' + restaurantId
			+ '&file=' + image, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	addCommandeTracabiliteImage(commande, image, restaurantId): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'commande_tracabilite?id=' + commande + '&restaurant_id=' + restaurantId,
			image, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	addCommandeLocalTracabiliteImage(commande, image, restaurantId): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'commande_local/' + commande + '?restaurant_id=' + restaurantId,
			image, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getFacturetteList(restaurantId): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'facturette?restaurant_id=' + restaurantId, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public downloadFacturette(restaurantId, id): any {
		return this.http.get<any>(this.apiUrl + 'facturette?id=' + id + '&download&restaurant_id=' + restaurantId, this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public downloadDepotFile(restaurantId, id): any {
		return this.http.get<any>(this.apiUrl + 'caisse/' + id + '?download&restaurant_id=' + restaurantId, this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	uploadFacturette(data, restaurantId): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'facturette?restaurant_id=' + restaurantId, data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	storeTips(data, restaurantId): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'tips?restaurant_id=' + restaurantId, data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getCommandeProduit(commande, restaurantId): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'commande_produit?id=' + commande + '&restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateCommandeProduit(commande, restaurantId, data): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'commande_produit?id=' + commande + '&restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	syncReceptionFournisseurIncidents(
		commandeId: number,
		restaurantId: number,
		incidents: unknown[],
	): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'fournisseur_incident?restaurant_id=' + restaurantId,
			{commande_id: commandeId, sync_reception: true, incidents},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createFournisseurIncident(restaurantId: number, payload: Record<string, unknown>): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'fournisseur_incident?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateFournisseurIncident(
		incidentId: number,
		restaurantId: number,
		payload: Record<string, unknown>,
	): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'fournisseur_incident/' + incidentId + '?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteFournisseurIncident(incidentId: number, restaurantId: number): Observable<any> {
		return this.http.delete<any>(
			this.apiUrl + 'fournisseur_incident/' + incidentId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurIncidents(
		restaurantId: number,
		from: string,
		to: string,
		fournisseurId?: number,
		context?: string,
	): Observable<any> {
		let url = this.apiUrl + 'fournisseur_incident?restaurant_id=' + restaurantId + '&from=' + from + '&to=' + to;
		if (fournisseurId) {
			url += '&fournisseur_id=' + fournisseurId;
		}
		if (context) {
			url += '&context=' + context;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getFournisseurIncidentsByCommande(commandeId: number, restaurantId: number): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'fournisseur_incident?restaurant_id=' + restaurantId + '&commande_id=' + commandeId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurIncident(incidentId: number, restaurantId: number): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'fournisseur_incident/' + incidentId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurIncidentStats(from: string, to: string, fournisseurId?: number): Observable<any> {
		let url = this.apiUrl + 'fournisseur_incident/stats?from=' + from + '&to=' + to;
		if (fournisseurId) {
			url += '&fournisseur_id=' + fournisseurId;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getFournisseurDemandes(
		restaurantId: number,
		status?: string,
		type?: string,
	): Observable<any> {
		let url = this.apiUrl + 'fournisseur_demande?restaurant_id=' + restaurantId;
		if (status) {
			url += '&status=' + status;
		}
		if (type) {
			url += '&type=' + type;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getFournisseurDemande(demandeId: number): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'fournisseur_demande/' + demandeId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurDemandeQueue(type?: string): Observable<any> {
		let url = this.apiUrl + 'fournisseur_demande/queue';
		if (type) {
			url += '?type=' + type;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getFournisseurDemandeZeltyMeta(restaurantId?: number): Observable<any> {
		let url = this.apiUrl + 'fournisseur_demande/zelty-meta';
		if (restaurantId) {
			url += '?restaurant_id=' + restaurantId;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createFournisseurDemande(restaurantId: number, data: Record<string, unknown>): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'fournisseur_demande?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	cancelFournisseurDemande(demandeId: number, restaurantId: number): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'fournisseur_demande/' + demandeId + '/cancel?restaurant_id=' + restaurantId,
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	approveFournisseurDemande(demandeId: number, adminOverrides?: Record<string, unknown>): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'fournisseur_demande/' + demandeId + '/approve',
			adminOverrides ? {admin_overrides: adminOverrides} : {},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	rejectFournisseurDemande(demandeId: number, adminComment?: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'fournisseur_demande/' + demandeId + '/reject',
			adminComment ? {admin_comment: adminComment} : {},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	addFournisseurIncidentPhoto(
		incidentId: number,
		restaurantId: number,
		data: { data: string; name: string },
	): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'fournisseur_incident/' + incidentId + '/photo?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurIncidentPhoto(
		incidentId: number,
		restaurantId: number,
		fileName: string,
	): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'fournisseur_incident/' + incidentId + '/photo?restaurant_id=' + restaurantId + '&file=' + encodeURIComponent(fileName),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	notifyFournisseurIncident(incidentId: number, restaurantId: number): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'fournisseur_incident/' + incidentId + '/notify?restaurant_id=' + restaurantId,
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurIncidentPublic(token: string): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'fournisseur_incident/public?token=' + encodeURIComponent(token),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurIncidentPublicPhoto(token: string, fileName: string): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'fournisseur_incident/public/photo?token=' + encodeURIComponent(token) + '&file=' + encodeURIComponent(fileName),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	submitFournisseurIncidentPublicResponse(token: string, payload: Record<string, unknown>): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'fournisseur_incident/public',
			{token, ...payload},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getCommandes(restaurantId: any) {
		return this.http.get<any>(this.apiUrl + 'commande?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getCommandesLocal(restaurantId: any) {
		return this.http.get<any>(this.apiUrl + 'commande_local?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getPendingCommandes(restaurantId: any) {
		return this.http.get<any>(this.apiUrl + 'commande?pending&restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getPendingDeliveriesForDate(restaurantId: number, date: string) {
		return this.http.get<any>(
			this.apiUrl + 'commande?pending_for_date&restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseur(id: any) {
		return this.http.get<any>(this.apiUrl + 'fournisseur?id=' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getFournisseurLinkedRestaurants(fournisseurId: number) {
		return this.http.get<any>(
			this.apiUrl + 'produit?fournisseur_id=' + fournisseurId + '&linked_restaurants=1',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFournisseurs() {
		return this.http.get<any>(this.apiUrl + 'fournisseur', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createFournisseur(payload: Record<string, unknown>) {
		return this.http.post<any>(this.apiUrl + 'fournisseur', payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getFournisseursForRestaurantAdd(restaurantId: number) {
		return this.http.get<any>(
			this.apiUrl + 'fournisseur?for_restaurant_add=1&restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateFournisseur(fournisseurId: number, payload: Record<string, unknown>) {
		return this.http.put<any>(
			this.apiUrl + 'fournisseur/' + fournisseurId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getFrigos(restaurantId: number) {
		return this.http.get<Frigo[]>(this.apiUrl + 'frigos?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(
				map(frigos => [...(frigos ?? [])].sort((a, b) =>
					(a.name ?? '').localeCompare(b.name ?? '', 'fr', { sensitivity: 'base' }),
				)),
				catchError(this.handleError),
			);
	}

	getHygieneExtincteurs(restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.hygienePath('extincteur', restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	saveHygieneExtincteur(restaurantId: number, payload: Record<string, unknown>, id?: number): Observable<any> {
		const url = this.hygienePath(id ? 'extincteur/' + id : 'extincteur', restaurantId);
		const request = id
			? this.http.put<any>(url, payload, this.getHttpOptions())
			: this.http.post<any>(url, payload, this.getHttpOptions());
		return request.pipe(catchError(this.handleError));
	}

	deleteHygieneExtincteur(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.hygienePath('extincteur/' + id, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	uploadHygieneExtincteurPdf(restaurantId: number, id: number, data: string): Observable<any> {
		return this.http.post<any>(this.hygienePath('extincteur/' + id + '/pdf', restaurantId), {data}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	downloadHygieneExtincteurPdf(restaurantId: number, id: number): Observable<Blob> {
		return this.http.get(this.hygienePath('extincteur/' + id + '/pdf', restaurantId), this.getBlobHttpOptions()) as unknown as Observable<Blob>;
	}

	getHygieneNuisibles(restaurantId: number): Observable<{ contrats: any[]; visites: any[] }> {
		return this.http.get<{ contrats: any[]; visites: any[] }>(this.hygienePath('nuisible', restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	saveHygieneContrat(restaurantId: number, payload: Record<string, unknown>, id?: number): Observable<any> {
		const url = this.hygienePath(id ? 'nuisible/contrat/' + id : 'nuisible/contrat', restaurantId);
		const request = id
			? this.http.put<any>(url, payload, this.getHttpOptions())
			: this.http.post<any>(url, payload, this.getHttpOptions());
		return request.pipe(catchError(this.handleError));
	}

	deleteHygieneContrat(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.hygienePath('nuisible/contrat/' + id, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createHygieneVisite(restaurantId: number, payload: Record<string, unknown>): Observable<any> {
		return this.http.post<any>(this.hygienePath('nuisible/visite', restaurantId), payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	deleteHygieneVisite(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.hygienePath('nuisible/visite/' + id, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	uploadHygieneVisitePdf(restaurantId: number, id: number, data: string): Observable<any> {
		return this.http.post<any>(this.hygienePath('nuisible/visite/' + id + '/pdf', restaurantId), {data}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	downloadHygieneVisitePdf(restaurantId: number, id: number): Observable<Blob> {
		return this.http.get(this.hygienePath('nuisible/visite/' + id + '/pdf', restaurantId), this.getBlobHttpOptions()) as unknown as Observable<Blob>;
	}

	getHygieneNettoyage(restaurantId: number, date: string): Observable<{ date: string; plans: any[] }> {
		return this.http.get<{ date: string; plans: any[] }>(
			this.hygienePath('nettoyage', restaurantId) + '&date=' + date,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	saveHygienePlan(restaurantId: number, payload: Record<string, unknown>, id?: number): Observable<any> {
		const url = this.hygienePath(id ? 'nettoyage/' + id : 'nettoyage', restaurantId);
		const request = id
			? this.http.put<any>(url, payload, this.getHttpOptions())
			: this.http.post<any>(url, payload, this.getHttpOptions());
		return request.pipe(catchError(this.handleError));
	}

	deleteHygienePlan(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.hygienePath('nettoyage/' + id, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	setHygienePlanDone(restaurantId: number, id: number, date: string, done: boolean): Observable<{ date: string; plans: any[] }> {
		const url = this.hygienePath('nettoyage/' + id + '/fait', restaurantId) + '&date=' + date;
		const request = done
			? this.http.post<{ date: string; plans: any[] }>(url, {date}, this.getHttpOptions())
			: this.http.delete<{ date: string; plans: any[] }>(url, this.getHttpOptions());
		return request.pipe(catchError(this.handleError));
	}

	private hygienePath(path: string, restaurantId: number): string {
		return this.apiUrl + 'hygiene/' + path + '?restaurant_id=' + restaurantId;
	}

	getLatestCaisse(restaurantId: number) {
		return this.http.get<backoffice_caisse>(this.apiUrl + 'caisse/latest?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getLatestValid(restaurantId: number) {
		return this.http.get<any>(this.apiUrl + 'caisse/valid?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getLatestComptages(restaurantId: number) {
		return this.http.get<any>(this.apiUrl + 'caisse/historique/comptage?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getBalance(restaurantId: number) {
		return this.http.get<any>(this.apiUrl + 'caisse/balance?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getLatestBanque(restaurantId: number) {
		return this.http.get<any>(this.apiUrl + 'caisse/historique/banque?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getCaisseMouvements(restaurantId: number, type?: string) {
		const from = new Date();
		from.setFullYear(from.getFullYear() - 1);
		const fromStr = from.toISOString().slice(0, 10);
		const toStr = new Date().toISOString().slice(0, 10);
		let url = this.apiUrl + 'caisse/mouvements?restaurant_id=' + restaurantId
			+ '&from=' + encodeURIComponent(fromStr)
			+ '&to=' + encodeURIComponent(toStr);
		if (type) {
			url += '&type=' + encodeURIComponent(type);
		}
		return this.http.get<any[]>(url, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createSortieCaisse(data: any): any {
		return this.http.post<any>(this.apiUrl + 'caisse/sortie', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getFrigosReleves(restaurantId: number) {
		return this.http.get<any>(this.apiUrl + 'releve_frigo?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	storeFrigoTemperature(restaurantId: number, id: number, value: number, comment: string, service?: string, moment?: string) {
		return this.http.post<any>(this.apiUrl + 'releve_frigo/' + id + '?restaurant_id=' + restaurantId,
			{'value': value, 'comment': comment, 'service': service, 'moment': moment},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateFrigo(restaurantId: number, id: any, data: any) {
		return this.http.put<any>(this.apiUrl + 'frigos/' + id + '?restaurant_id=' + restaurantId, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createFrigo(restaurantId: number, data: any) {
		return this.http.post<any>(this.apiUrl + 'frigos?restaurant_id=' + restaurantId, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getUserAlerts() {
		return this.http.get<any>(this.apiUrl + 'alert', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getAlertTasks(restaurantId: number) {
		return this.http.get<any>(this.apiUrl + 'alert/tasks?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	validateAlertTask(data: { alert_id: number, task_id: number, restaurant_id: number }) {
		return this.http.post<any>(this.apiUrl + 'alert/task_log', data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getAlerts() {
		return this.http.get<any>(this.apiUrl + 'alert/all', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getAlert(id: number) {
		return this.http.get<any>(this.apiUrl + 'alert/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getAlertsView(id: number) {
		return this.http.get<any>(this.apiUrl + 'alert_view/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getAlertSeriesStats(id: number) {
		return this.http.get<any>(this.apiUrl + 'alert/' + id + '/series-stats', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createAlert(data: any) {
		return this.http.post<any>(this.apiUrl + 'alert', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	deleteAlert(id: number) {
		return this.http.delete<any>(this.apiUrl + 'alert/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createAlertView(id: number, type: 'ACK' | 'VIEW') {
		return this.http.post<any>(this.apiUrl + 'alert_view/' + id, {'type': type},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getChangelogPending() {
		return this.http.get<any>(this.apiUrl + 'changelog/pending', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getChangelogPendingCount() {
		return this.http.get<{ count: number }>(this.apiUrl + 'changelog/pending/count', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getChangelogHistory(limit = 50, offset = 0) {
		return this.http.get<any>(this.apiUrl + 'changelog?limit=' + limit + '&offset=' + offset, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getChangelogRelease(id: number) {
		return this.http.get<any>(this.apiUrl + 'changelog/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	ackChangelogRelease(id: number) {
		return this.http.post<any>(this.apiUrl + 'changelog/' + id + '/ack', {}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getChangelogAdminAll(limit = 50) {
		return this.http.get<any>(this.apiUrl + 'changelog/admin/all?limit=' + limit, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getChangelogAckStats(id: number) {
		return this.http.get<any>(this.apiUrl + 'changelog/' + id + '/acks', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createChangelogRelease(data: any) {
		return this.http.post<any>(this.apiUrl + 'changelog', data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateChangelogRelease(id: number, data: any) {
		return this.http.put<any>(this.apiUrl + 'changelog/' + id, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	deleteChangelogRelease(id: number) {
		return this.http.delete<any>(this.apiUrl + 'changelog/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	previewSupportPush(data: any) {
		return this.http.post<any>(this.apiUrl + 'support_push/preview', data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	sendSupportPush(data: any) {
		return this.http.post<any>(this.apiUrl + 'support_push', data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getSupportPushPushableUsers() {
		return this.http.get<any[]>(this.apiUrl + 'support_push/pushable_users', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getSupportPushList(limit = 50, offset = 0) {
		return this.http.get<any>(
			this.apiUrl + 'support_push?limit=' + limit + '&offset=' + offset,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getSupportPush(id: number) {
		return this.http.get<any>(this.apiUrl + 'support_push/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getPermissions() {
		return this.http.get<any>(this.apiUrl + 'user/permissions', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getFournisseurRestaurant(restoId: number, id?: number) {
		return this.http.get<any>(this.apiUrl + 'fournisseur_restaurant?restaurant_id=' + restoId + (id ? '&id=' + id : ''),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getNeededStock(fournisseurId: number, restaurant_id: number) {
		return this.http.get<any>(this.apiUrl + 'needed_stock?restaurant_id=' + restaurant_id + (fournisseurId ? ('&id=' + fournisseurId) : ''),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createNeededStock(data: any) {
		return this.http.post<any>(this.apiUrl + 'needed_stock', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateNeededStock(data): any {
		return this.http.put<any>(this.apiUrl + 'needed_stock/' + data.id, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getPrepareCommande(
		fournisseurId: number | null,
		restaurant_id: number,
		options?: { getWithoutNeededStock?: boolean },
	) {
		let url = `${this.apiUrl}prepare_commande?restaurant_id=${restaurant_id}`;
		if (fournisseurId) {
			url += `&fournisseur_id=${fournisseurId}`;
		}
		if (options?.getWithoutNeededStock) {
			url += '&get_without_needed_stock=1';
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getStock(fournisseurId: number, restaurant_id: number) {
		return this.http.get<any>(this.apiUrl + 'stock?restaurant_id=' + restaurant_id + (fournisseurId ? ('&id=' + fournisseurId) : ''),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getProduits(fournisseurId: number, restaurant_id: number, all = false): any {
		const params: string[] = [];
		if (fournisseurId) {
			params.push('fournisseur_id=' + fournisseurId);
		}
		if (restaurant_id) {
			params.push('restaurant_id=' + restaurant_id);
		}
		if (all) {
			params.push('all=true');
		}
		const query = params.length ? '?' + params.join('&') : '';
		return this.http.get<any>(this.apiUrl + 'produit' + query, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getInventoryZones(
		fournisseurId: number,
		restaurantId: number,
	): Observable<{zones: InventoryZoneLayout[]; unassigned_product_ids: number[]}> {
		return this.http.get<{zones: InventoryZoneLayout[]; unassigned_product_ids: number[]}>(
			this.apiUrl + 'inventory_zone?fournisseur_id=' + fournisseurId + '&restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public saveInventoryZones(
		fournisseurId: number,
		restaurantId: number,
		zones: InventoryZoneSavePayload[],
	): Observable<{zones: InventoryZoneLayout[]; unassigned_product_ids: number[]}> {
		return this.http.put<{zones: InventoryZoneLayout[]; unassigned_product_ids: number[]}>(
			this.apiUrl + 'inventory_zone',
			{fournisseur_id: fournisseurId, restaurant_id: restaurantId, zones},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getProduitCategories(
		fournisseurId: number,
	): Observable<{categories: ProduitCategoryLayout[]; unassigned_product_ids: number[]}> {
		return this.http.get<{categories: ProduitCategoryLayout[]; unassigned_product_ids: number[]}>(
			this.apiUrl + 'produit_category?fournisseur_id=' + fournisseurId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public saveProduitCategories(
		fournisseurId: number,
		categories: ProduitCategorySavePayload[],
	): Observable<{categories: ProduitCategoryLayout[]; unassigned_product_ids: number[]}> {
		return this.http.put<{categories: ProduitCategoryLayout[]; unassigned_product_ids: number[]}>(
			this.apiUrl + 'produit_category',
			{fournisseur_id: fournisseurId, categories},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getUsersByRestaurant(restaurantId: number, full = false) {
		return this.http.get<any[]>(this.apiUrl + 'user_restaurant?restaurant_id=' + restaurantId + (full ? '&full=true' : ''),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getArchivedUsersByRestaurant(restaurantId: number) {
		return this.http.get<any[]>(this.apiUrl + 'user_restaurant?restaurant_id=' + restaurantId + '&archived=1&full=true',
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	archiveUserRestaurant(restaurantId: number, userId: number) {
		return this.http.post<any>(this.apiUrl + 'user_restaurant/' + userId + '/archive?restaurant_id=' + restaurantId, {},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	reactivateUserRestaurant(restaurantId: number, userId: number) {
		return this.http.post<any>(this.apiUrl + 'user_restaurant/' + userId + '/reactivate?restaurant_id=' + restaurantId, {},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getRestaurantByUser(userId: number) {
		return this.http.get<any[]>(this.apiUrl + 'user_restaurant?user_id=' + userId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getContracts(restaurantId?: number) {
		return this.http.get<any[]>(this.apiUrl + 'contracts' + (restaurantId ? ('?restaurant_id=' + restaurantId) : ''),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteContract(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'contracts/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPlanning(restaurantId: number, startDate, endDate, ungrouped = false): any {
		return this.http.get<any>(this.apiUrl + 'planning?restaurant_id=' + restaurantId + '&startDate=' + startDate + '&endDate=' + endDate + (ungrouped ? '&ungrouped=true' : ''),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getWaitingPlanning(restaurantId: number): any {
		return this.http.get<any>(this.apiUrl + 'planning/waiting?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getAvailablePlanning(): any {
		return this.http.get<any>(this.apiUrl + 'planning/available',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getMyPlanning(): any {
		return this.http.get<any>(this.apiUrl + 'planning/me',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createCreneau(data: any) {
		return this.http.post<any>(this.apiUrl + 'planning', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateCreneau(data): any {
		return this.http.put<any>(this.apiUrl + 'planning/' + data.id, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createProduit(data: any, restaurantId?: number) {
		const url = this.apiUrl + 'produit' + (restaurantId ? '?restaurant_id=' + restaurantId : '');
		return this.http.post<any>(url, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateProduit(data, restaurantId?: number): any {
		const url = this.apiUrl + 'produit/' + data.id + (restaurantId ? '?restaurant_id=' + restaurantId : '');
		return this.http.put<any>(url, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public upsertProduitRestaurantPrice(productId: number, price: number, restaurantId?: number): Observable<any> {
		let url = this.apiUrl + 'produit/' + productId + '/restaurant-price';
		if (restaurantId) {
			url += '?restaurant_id=' + restaurantId;
		}
		return this.http.put<any>(url, { price }, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public resetProduitRestaurantPrice(productId: number, restaurantId?: number): Observable<any> {
		let url = this.apiUrl + 'produit/restaurant-price/' + productId;
		if (restaurantId) {
			url += '?restaurant_id=' + restaurantId;
		}
		return this.http.delete<any>(url, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getProduitFinals(all: boolean = false): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'produit_final' + (all ? '?all=true' : ''),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getProduitFinal(id: number): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'produit_final/' + id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getProduitFinalTags(): Observable<Array<{ id: number; name: string }>> {
		return this.http.get<Array<{ id: number; name: string }>>(
			this.apiUrl + 'produit_final/tags',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getCatalog(restaurantId?: number, traiteur = false): Observable<any[]> {
		let url = this.apiUrl + 'catalog';
		const params: string[] = [];
		if (restaurantId) {
			params.push('restaurant_id=' + restaurantId);
		}
		if (traiteur) {
			params.push('traiteur=1');
		}
		if (params.length > 0) {
			url += '?' + params.join('&');
		}
		return this.http.get<any[]>(url,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getTheoreticalConsumption(params: { date_start?: string, date_end?: string, inventory_start?: string, inventory_end?: string }, restaurantId?: number): Observable<any> {
		let url = this.apiUrl + 'theoretical-consumption?';
		if (params.date_start) {
			url += 'date_start=' + params.date_start + '&date_end=' + params.date_end;
		} else if (params.inventory_start) {
			url += 'inventory_start=' + params.inventory_start + '&inventory_end=' + params.inventory_end;
		}
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getProductLosses(
		from: string,
		to: string,
		productId?: number,
		restaurantId?: number,
	): Observable<any[]> {
		let url = this.apiUrl + 'product_loss?from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to);
		if (productId) {
			url += '&product_id=' + productId;
		}
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		return this.http.get<any[]>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getProductLossTracabilite(
		productId: number,
		restaurantId: number,
	): Observable<Array<{
		commande_id: number;
		received_at: string | null;
		photos: Array<{ filename: string }>;
	}>> {
		return this.http.get<Array<{
			commande_id: number;
			received_at: string | null;
			photos: Array<{ filename: string }>;
		}>>(
			this.apiUrl
				+ 'product_loss/tracabilite?product_id='
				+ productId
				+ '&restaurant_id='
				+ restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public createProductLoss(
		payload: {
			product_id: number;
			quantity: number;
			reason: string;
			comment?: string;
			occurred_at?: string;
		},
		restaurantId: number,
	): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'product_loss?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public deleteProductLoss(id: number, restaurantId: number): Observable<any> {
		return this.http.delete<any>(
			this.apiUrl + 'product_loss/' + id + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getGlobalInventory(restaurantId?: number): Observable<any[]> {
		return this.getInventories(restaurantId, 'global');
	}

	public getInventories(restaurantId?: number, type: 'global' | 'fournisseur' | 'all' = 'all'): Observable<any[]> {
		let url = this.apiUrl + 'global-inventory?type=' + encodeURIComponent(type);
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		return this.http.get<any[]>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public downloadGlobalInventoryExport(inventaireId: string, restaurantId?: number): Observable<Blob> {
		let url = this.apiUrl + 'global-inventory/' + encodeURIComponent(inventaireId) + '/export';
		if (restaurantId) {
			url += '?restaurant_id=' + restaurantId;
		}
		return this.http.get(url, this.getBlobHttpOptions()) as unknown as Observable<Blob>;
	}

	public getStockAtDate(
		date: string | Date,
		restaurantId?: number,
		filters?: { product_id?: number; fournisseur_id?: number },
	): Observable<any> {
		const dateStr = date instanceof Date
			? date.toISOString().slice(0, 10)
			: date;
		let url = this.apiUrl + 'stock-at-date?date=' + encodeURIComponent(dateStr);
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		if (filters?.product_id) {
			url += '&product_id=' + filters.product_id;
		}
		if (filters?.fournisseur_id) {
			url += '&fournisseur_id=' + filters.fournisseur_id;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public listPeriodStats(restaurantId?: number): Observable<{ months: Array<{ month: string; ca: number; couverts: number; fondues: number; desserts: number }> }> {
		let url = this.apiUrl + 'period-stats';
		if (restaurantId) {
			url += '?restaurant_id=' + restaurantId;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getPeriodStats(month: string, restaurantId?: number): Observable<any> {
		let url = this.apiUrl + 'period-stats?month=' + encodeURIComponent(month);
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public savePeriodStats(
		month: string,
		data: { ca: number; couverts: number; fondues: number; desserts: number },
		restaurantId?: number,
	): Observable<any> {
		const body: any = {month, ...data};
		if (restaurantId) {
			body.restaurant_id = restaurantId;
		}
		return this.http.put<any>(this.apiUrl + 'period-stats', body, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public deletePeriodStats(month: string, restaurantId?: number): Observable<any> {
		let url = this.apiUrl + 'period-stats?month=' + encodeURIComponent(month);
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		return this.http.delete<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public linkProduitFinalCatalog(
		catalogItemId: string | number,
		produitFinalId: number,
		options?: { restaurantId?: number; isDefault?: boolean },
	): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'produit_final/link-catalog', {
			catalog_item_id: String(catalogItemId),
			produit_final_id: produitFinalId,
			restaurant_id: options?.restaurantId ?? null,
			is_default: options?.isDefault ?? false,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public unlinkProduitFinalCatalog(catalogItemId: string | number, produitFinalId?: number): Observable<any> {
		let url = this.apiUrl + 'produit_final/link-catalog/' + encodeURIComponent(String(catalogItemId));
		if (produitFinalId != null) {
			url += '?produit_final_id=' + produitFinalId;
		}
		return this.http.delete<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public linkCatalogProduct(
		catalogItemId: string | number,
		productId: number,
		quantity: number,
		options?: { restaurantId?: number; isDefault?: boolean },
	): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'produit_final/link-product', {
			catalog_item_id: String(catalogItemId),
			product_id: productId,
			quantity,
			restaurant_id: options?.restaurantId ?? null,
			is_default: options?.isDefault ?? false,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCatalogItemProducts(): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'catalog_item_product', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getCatalogLinkSupplierProducts(
		restaurantId?: number,
		options?: { forLoss?: boolean; includeProductId?: number },
	): Observable<any[]> {
		const params: string[] = [];
		if (restaurantId) {
			params.push('restaurant_id=' + restaurantId);
		}
		if (options?.forLoss) {
			params.push('for_loss=1');
		}
		if (options?.includeProductId) {
			params.push('include_product_id=' + options.includeProductId);
		}
		let url = this.apiUrl + 'catalog_item_product/supplier-options';
		if (params.length) {
			url += '?' + params.join('&');
		}
		return this.http.get<any[]>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateCatalogItemProduct(catalogItemId: string | number, productId: number, quantity: number): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'catalog_item_product', {
			catalog_item_id: String(catalogItemId),
			product_id: productId,
			quantity,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCatalogItemLinks(catalogItemId: string | number, restaurantId?: number): Observable<any> {
		let url = this.apiUrl + 'catalog_item_product/' + encodeURIComponent(String(catalogItemId)) + '?links=1';
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public setCatalogRestaurantLink(
		catalogItemId: string | number,
		restaurantId: number,
		payload: { link_type: 'direct' | 'produit_final'; product_id?: number; quantity?: number; produit_final_id?: number },
	): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'catalog_item_product', {
			catalog_item_id: String(catalogItemId),
			restaurant_id: restaurantId,
			...payload,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public clearCatalogRestaurantLink(catalogItemId: string | number, restaurantId: number): Observable<any> {
		return this.http.delete<any>(
			this.apiUrl + 'catalog_item_product/' + encodeURIComponent(String(catalogItemId)) + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public deleteCatalogItemProduct(catalogItemId: string | number, productId?: number): Observable<any> {
		let url = this.apiUrl + 'catalog_item_product/' + encodeURIComponent(String(catalogItemId));
		if (productId != null) {
			url += '?product_id=' + productId;
		}
		return this.http.delete<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public ignoreCatalogItem(catalogItemId: string | number, catalogItemName?: string | null): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'catalog_item_ignored', {
			catalog_item_id: String(catalogItemId),
			catalog_item_name: catalogItemName ?? null,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public unignoreCatalogItem(catalogItemId: string | number): Observable<any> {
		return this.http.delete<any>(
			this.apiUrl + 'catalog_item_ignored/' + encodeURIComponent(String(catalogItemId)),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public restoreAutoIgnoredParentCatalogItem(
		catalogItemId: string | number,
		catalogItemName?: string | null,
	): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'catalog_item_parent_restored', {
			catalog_item_id: String(catalogItemId),
			catalog_item_name: catalogItemName ?? null,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createProduitFinal(data: any): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'produit_final', this.formatProduitFinalPayload(data),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateProduitFinal(data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'produit_final/' + data.id, this.formatProduitFinalPayload(data),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	private formatProduitFinalPayload(data: any): any {
		const payload = {...data};
		if (Array.isArray(data.tag_names)) {
			payload.tag_names = data.tag_names.filter((name: string) => !!String(name || '').trim());
		} else if (Array.isArray(data.tags)) {
			payload.tag_names = data.tags
				.map((tag: string | { name?: string }) => (typeof tag === 'string' ? tag : tag?.name))
				.filter((name: string) => !!String(name || '').trim());
		}
		delete payload.tags;
		return payload;
	}

	public duplicateProduitFinal(id: number): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'produit_final/' + id + '/duplicate', {},
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public calculateProduitFinal(data: { product_id: number, quantity: number }): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'produit_final/calculate', data,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public deleteCreneau(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'planning/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPlanningTemplate(restaurantId: number): any {
		return this.http.get<any>(this.apiUrl + 'planning_template?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createCreneauTemplate(data: any) {
		return this.http.post<any>(this.apiUrl + 'planning_template', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateCreneauTemplate(data): any {
		return this.http.put<any>(this.apiUrl + 'planning_template/' + data.id, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteCreneauTemplate(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'planning_template/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getMonthEmployees(restaurantId, month): any {
		return this.http.get<any>(this.apiUrl + 'presential?restaurant_id=' + restaurantId + '&month=' + month,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getDateEmployees(restaurantId, date): any {
		return this.http.get<any>(this.apiUrl + 'presential?restaurant_id=' + restaurantId + '&date=' + date,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getMonthEmployeesTips(restaurantId, startDate, endDate): any {
		return this.http.get<any>(this.apiUrl + 'presential/tips?restaurant_id=' + restaurantId + '&startDate=' + startDate + '&endDate=' + endDate,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getEmployeesRestaurantHistory(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'presential/restaurant-history?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getEmployeesRestaurantTypeHistory(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'presential/restaurant-type-history?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createPresential(data: any) {
		return this.http.post<any>(this.apiUrl + 'presential', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updatePresential(data): any {
		return this.http.put<any>(this.apiUrl + 'presential/' + data.id, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deletePresential(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'presential/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getInvitation(token: any): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'invitation?token=' + token,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public listInvitations(restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'invitation?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createUser(data) {
		return this.http.post<any>(this.apiUrl + 'user', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPermissionsList(): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'permissions',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createInvitation(restaurantId, data) {
		return this.http.post<any>(this.apiUrl + 'invitation?restaurant_id=' + restaurantId, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public resendInvitation(invitationId: number): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'invitation/' + invitationId + '/resend', {},
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public deleteInvitation(invitationId: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'invitation/' + invitationId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public editUserRestaurant(restaurantId, data): any {
		return this.http.put<any>(this.apiUrl + 'user_restaurant/' + data.id + '?restaurant_id=' + restaurantId, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public addUserRestaurant(data): any {
		return this.http.post<any>(this.apiUrl + 'user_restaurant/' + data.user_id, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteUserRestaurant(restaurantId, id): any {
		return this.http.delete<any>(this.apiUrl + 'user_restaurant/' + id + '?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public signContract(id, data) {
		return this.http.post<any>(this.apiUrl + 'contracts/' + id + '/sign', data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public sendContract(id) {
		return this.http.get<any>(this.apiUrl + 'contracts/' + id + '/contract-send', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public downloadRecapHours(month, restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'presential/export?month=' + month + '&restaurant_id=' + restaurantId, this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public downloadSignedContract(id): any {
		return this.http.get<any>(this.apiUrl + 'contracts/' + id + '/download-signed', this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public downloadContract(id): any {
		return this.http.get<any>(this.apiUrl + 'contracts/' + id + '/download', this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public downloadGeneratedContract(id): any {
		//		return this.http.get<any>('https://api.groin.le17-45.fr/generate-contract.php?id=' + id + '', options)
		return this.http.get<any>(this.apiUrl + 'contracts/' + id + '/contract-generate', this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getContract(id): any {
		return this.http.get<any>(this.apiUrl + 'contracts/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getContractPresential(id): any {
		return this.http.get<any>(this.apiUrl + 'contracts/' + id + '/presential',
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getDocuments(restaurantId) {
		return this.http.get<any>(this.apiUrl + 'documents?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));

	}

	public getDocument(restaurantId, file) {
		return this.http.get<any>(this.apiUrl + 'documents?restaurant_id=' + restaurantId + '&file=' + encodeURIComponent(file),
			this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteDocument(restaurantId, file) {
		return this.http.delete<any>(this.apiUrl + 'documents?restaurant_id=' + restaurantId + '&file=' + encodeURIComponent(file),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createDocumentCategory(restaurantId, categoryName) {
		return this.http.post<any>(this.apiUrl + 'documents?restaurant_id=' + restaurantId,
			{'name': categoryName},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public renameDocumentCategory(restaurantId, oldName, categoryName) {
		return this.http.post<any>(this.apiUrl + 'documents?restaurant_id=' + restaurantId,
			{'old_name': oldName, 'name': categoryName},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public uploadDocument(data, restaurantId) {
		return this.http.post<any>(this.apiUrl + 'documents?restaurant_id=' + restaurantId,
			data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public sendDocumentByEmail(restaurantId, file, destinataireEmail, subject, message) {
		return this.http.post<any>(this.apiUrl + 'documents/send?restaurant_id=' + restaurantId,
			{file, destinataire_email: destinataireEmail, subject, message},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getDocumentMailTemplates(restaurantId) {
		return this.http.get<any>(this.apiUrl + 'documents/templates?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createDocumentMailTemplate(restaurantId, payload) {
		return this.http.post<any>(this.apiUrl + 'documents/templates?restaurant_id=' + restaurantId,
			payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateDocumentMailTemplate(restaurantId, templateId, payload) {
		return this.http.put<any>(this.apiUrl + 'documents/templates/' + templateId + '?restaurant_id=' + restaurantId,
			payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteDocumentMailTemplate(restaurantId, templateId) {
		return this.http.delete<any>(this.apiUrl + 'documents/templates/' + templateId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	uploadContract(id: any, restaurantId, data) {
		return this.http.post<any>(this.apiUrl + 'contracts/' + id + '/upload?restaurant_id=' + restaurantId,
			data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getRestaurant(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'restaurants?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updatePrecommandePersonnesParPlanche(restaurantId, personnes: number): any {
		return this.http.put<any>(
			this.apiUrl + 'restaurants/' + restaurantId + '/precommande-personnes-par-planche?restaurant_id=' + restaurantId,
			{precommande_personnes_par_planche: personnes},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPrecommandePlanches(restaurantId): any {
		return this.http.get<any>(
			this.apiUrl + 'restaurants/' + restaurantId + '/precommande-planches?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updatePrecommandePlanches(restaurantId, items): any {
		return this.http.put<any>(
			this.apiUrl + 'restaurants/' + restaurantId + '/precommande-planches?restaurant_id=' + restaurantId,
			{items},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPrecommandeCatalog(): any {
		return this.http.get<any>(this.apiUrl + 'precommande-catalog', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createPrecommandeCatalogProduct(payload): any {
		return this.http.post<any>(this.apiUrl + 'precommande-catalog', payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updatePrecommandeCatalogProduct(slug: string, payload): any {
		return this.http.put<any>(
			this.apiUrl + 'precommande-catalog/' + encodeURIComponent(slug),
			payload,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateBoutiqueInvoiceHt(restaurantId, enabled: boolean): any {
		return this.http.put<any>(
			this.apiUrl + 'restaurants/' + restaurantId + '/boutique-invoice-ht?restaurant_id=' + restaurantId,
			{boutique_invoice_ht: enabled ? 1 : 0},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getOpeningHours(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'restaurant_opening_hours?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getOpeningHoursForDay(restaurantId: number, date: string): any {
		return this.http.get<any>(this.apiUrl + 'restaurant_opening_hours/day?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getCaisseDaySlots(restaurantId: number, date: string): any {
		return this.http.get<any>(this.apiUrl + 'caisse/day-slots?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getLatestTaggedCaisse(restaurantId: number): any {
		return this.http.get<any>(this.apiUrl + 'caisse/latest-tagged?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getFrigoDaySlots(restaurantId: number, date: string): any {
		return this.http.get<any>(this.apiUrl + 'releve_frigo/day-slots?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getLatestTaggedFrigoReleve(restaurantId: number): any {
		return this.http.get<any>(this.apiUrl + 'releve_frigo/latest-tagged?restaurant_id=' + restaurantId,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getSuiviPain(restaurantId: number, date: string): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'suivi-pain?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getSuiviPainDaySlots(restaurantId: number, date: string): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'suivi-pain/day-slots?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getSuiviPainStats(restaurantId: number, from: string, to: string): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'suivi-pain/stats?restaurant_id=' + restaurantId
				+ '&from=' + encodeURIComponent(from)
				+ '&to=' + encodeURIComponent(to),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public saveSuiviPain(restaurantId: number, payload: any): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'suivi-pain?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getServiceRitualStatus(restaurantId: number, date?: string): Observable<any> {
		let url = this.apiUrl + 'service-ritual/status?restaurant_id=' + restaurantId;
		if (date) {
			url += '&date=' + encodeURIComponent(date);
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public setServiceRitualMandatory(restaurantId: number, mandatory: boolean): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'service-ritual/mandatory?restaurant_id=' + restaurantId,
			{service_ritual_mandatory: mandatory ? 1 : 0},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getFreeTables(restaurantId: number, datetime: string, service?: string): Observable<any> {
		let url = this.apiUrl + 'restaurant_table_map/free-tables?restaurant_id=' + restaurantId
			+ '&datetime=' + encodeURIComponent(datetime);
		if (service) {
			url += '&service=' + encodeURIComponent(service);
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getTableMapOccupancy(restaurantId: number, datetime: string, service?: string, testMode = false): Observable<any> {
		let url = this.apiUrl + 'restaurant_table_map/occupancy?restaurant_id=' + restaurantId
			+ '&datetime=' + encodeURIComponent(datetime);
		if (service) {
			url += '&service=' + encodeURIComponent(service);
		}
		if (testMode) {
			url += '&test=1';
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public blockTableMapTable(
		restaurantId: number,
		tableId: number,
		datetime: string,
		service?: string,
		note?: string,
	): Observable<any> {
		const body: any = {table_id: tableId, datetime};
		if (service) {
			body.service = service;
		}
		if (note) {
			body.note = note;
		}
		return this.http.post<any>(
			this.apiUrl + 'restaurant_table_map/block/create?restaurant_id=' + restaurantId,
			body,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public unblockTableMapTable(restaurantId: number, blockId: number): Observable<any> {
		return this.http.delete<any>(
			this.apiUrl + 'restaurant_table_map/block/' + blockId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public assignReservationTables(restaurantId: number, reservationId: number, tableIds: number[]): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'restaurant_table_map/assignment/' + reservationId + '?restaurant_id=' + restaurantId,
			{table_ids: tableIds},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public deleteOpeningHours(id): any {
		return this.http.delete<any>(this.apiUrl + 'restaurant_opening_hours/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getOldReservations(restaurantId, email, telephone, id): any {
		return this.http.get<any>(this.apiUrl + 'reservation/client?restaurant_id=' + restaurantId + '&id=' + id + '&email=' + encodeURIComponent(email) + '&telephone=' + encodeURIComponent(telephone), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPendingReservations(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'reservation/new?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPendingDepositReservations(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'reservation/deposit?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getTransmissions(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'transmission?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public addTransmission(item: any): any {
		return this.http.post<any>(this.apiUrl + 'transmission', item,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getDateReservations(restaurantId, date): any {
		return this.http.get<any>(this.apiUrl + 'reservation/date/' + date + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateUser(item: any): any {
		return this.http.put<any>(this.apiUrl + 'user/' + item.id, item,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateOpeningHours(item: any): any {
		return this.http.put<any>(this.apiUrl + 'restaurant_opening_hours/' + item.id, item,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateFournisseurRestaurant(item: any): any {
		const restaurantId = item?.restaurant_id;
		const url = this.apiUrl + 'fournisseur_restaurant/' + item.id
			+ (restaurantId ? ('?restaurant_id=' + restaurantId) : '');
		return this.http.put<any>(url, item, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createOpeningHours(item: any): any {
		return this.http.post<any>(this.apiUrl + 'restaurant_opening_hours', item,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public cancelCommande(data: any): any {
		return this.http.post<any>(this.apiUrl + 'commande/' + data.id + '/cancel', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createCommande(restaurantId: number, fournisseurId: number, order): any {
		return this.http.post<any>(this.apiUrl + 'commande/' + fournisseurId + '?restaurant_id=' + restaurantId, order,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createStock(data: any): any {
		return this.http.post<any>(this.apiUrl + 'stock', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));

	}

	public getFullRestaurant(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'full_restaurant?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getFullRestaurantToday(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'full_restaurant?today&restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getBlockedResaRestaurant(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'reservation_auto_blocked?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getReservationAutoValidationConfig(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'reservation_auto_validation?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateAutoResaRestaurant(data: any): any {
		return this.http.put<any>(this.apiUrl + 'reservation_auto_validation/' + data.id, data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public addAutoResaRestaurant(data: any): any {
		return this.http.post<any>(this.apiUrl + 'reservation_auto_validation', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public addBlockedResaRestaurant(data: any): any {
		return this.http.post<any>(this.apiUrl + 'reservation_auto_blocked', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteBlockedResaRestaurant(id: any): any {
		return this.http.delete<any>(this.apiUrl + 'reservation_auto_blocked/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getRestaurantTableMap(restaurantId: number, testMode = false): Observable<any> {
		let url = this.apiUrl + 'restaurant_table_map?restaurant_id=' + restaurantId;
		if (testMode) {
			url += '&test=1';
		}
		return this.http.get<any>(url, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateRestaurantTableMapConfig(restaurantId: number, autoAssignmentEnabled: boolean): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/config?restaurant_id=' + restaurantId, {
			auto_assignment_enabled: autoAssignmentEnabled ? 1 : 0,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createRestaurantFloorPlan(restaurantId: number, name: string, floorId: number | null = null, testMode = false): Observable<any> {
		const payload: any = {name};
		if (floorId != null) {
			payload.floor_id = floorId;
		}
		if (testMode) {
			payload.is_test = 1;
		}
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/floor_plan/create?restaurant_id=' + restaurantId, payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateRestaurantFloorPlan(restaurantId: number, id: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/floor_plan/' + id + '?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteRestaurantFloorPlan(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'restaurant_table_map/floor_plan/' + id + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public previewRestaurantFloorPlanZelty(restaurantId: number, id: number, zoneLayoutMode = 'zelty_exact'): Observable<any> {
		const params = 'restaurant_id=' + restaurantId + '&zone_layout_mode=' + encodeURIComponent(zoneLayoutMode);
		return this.http.get<any>(
			this.apiUrl + 'restaurant_table_map/floor_plan/' + id + '/zelty_preview?' + params,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public syncRestaurantFloorPlan(
		restaurantId: number,
		id: number,
		options: {
			reset_layout?: boolean;
			zone_layout_mode?: string;
			room_placements?: any[];
		} = {},
	): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/floor_plan/' + id + '/sync?restaurant_id=' + restaurantId, {
			reset_layout: options.reset_layout !== false ? 1 : 0,
			zone_layout_mode: options.zone_layout_mode ?? 'zelty_exact',
			room_placements: options.room_placements ?? [],
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createRestaurantTableMapFloor(restaurantId: number, name: string, displayOrder = 0): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/floor/create?restaurant_id=' + restaurantId, {
			name,
			display_order: displayOrder,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateRestaurantTableMapFloor(restaurantId: number, id: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/floor/' + id + '?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteRestaurantTableMapFloor(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'restaurant_table_map/floor/' + id + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createRestaurantFloorPlanDecoration(restaurantId: number, data: any): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/decoration/create?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateRestaurantFloorPlanDecoration(restaurantId: number, id: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/decoration/' + id + '?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteRestaurantFloorPlanDecoration(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'restaurant_table_map/decoration/' + id + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateRestaurantFloorPlanRoomZone(restaurantId: number, id: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/room_zone/' + id + '?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public duplicateRestaurantFloorPlan(restaurantId: number, id: number, name: string): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/floor_plan/' + id + '/duplicate?restaurant_id=' + restaurantId, {name}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public uploadRestaurantFloorPlanBackground(restaurantId: number, id: number, file: File): Observable<any> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/floor_plan/' + id + '/background?restaurant_id=' + restaurantId, formData, this.getMultipartHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveRestaurantFloorPlanLayout(
		restaurantId: number,
		id: number,
		tables: any[],
		decorations: any[] = [],
		roomZones: any[] = [],
	): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/floor_plan/' + id + '/layout?restaurant_id=' + restaurantId, {
			tables,
			decorations,
			room_zones: roomZones,
		}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateRestaurantTable(restaurantId: number, id: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/table/' + id + '?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createRestaurantTable(restaurantId: number, data: any): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/table/create?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteRestaurantTable(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'restaurant_table_map/table/' + id + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createRestaurantTableGroup(restaurantId: number, data: any): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/group/create?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateRestaurantTableGroup(restaurantId: number, id: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/group/' + id + '?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteRestaurantTableGroup(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'restaurant_table_map/group/' + id + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createRestaurantFloorPlanSchedule(restaurantId: number, data: any): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'restaurant_table_map/schedule/create?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateRestaurantFloorPlanSchedule(restaurantId: number, id: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'restaurant_table_map/schedule/' + id + '?restaurant_id=' + restaurantId, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteRestaurantFloorPlanSchedule(restaurantId: number, id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'restaurant_table_map/schedule/' + id + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public simulateRestaurantTableSlot(restaurantId: number, date: string, service: string, places: number): Observable<any> {
		const params = 'restaurant_id=' + restaurantId
			+ '&date=' + encodeURIComponent(date)
			+ '&service=' + encodeURIComponent(service)
			+ '&places=' + places;
		return this.http.get<any>(this.apiUrl + 'restaurant_table_map/simulate?' + params, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getReservationTableAssignment(restaurantId: number, reservationId: number): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'restaurant_table_map/assignment/' + reservationId + '?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError((error: HttpErrorResponse) => {
				// Pas d'assignation de table = cas métier courant, pas une erreur applicative.
				if (error.status === 404) {
					return of(null);
				}
				return this.handleError(error);
			}));
	}

	public getRestaurantFloorPlanBackground(restaurantId: number, floorPlanId: number, file: string): Observable<Blob> {
		const url = this.apiUrl + 'restaurant_table_map/background?restaurant_id=' + restaurantId
			+ '&floor_plan_id=' + floorPlanId + '&file=' + encodeURIComponent(file);
		return this.http.get(url, this.getBlobHttpOptions()) as unknown as Observable<Blob>;
	}

	public getAvisToAnswer(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'avis/to_answer?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getAvisLatest(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'avis/latest?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getAvisStats(restaurantId): any {
		return this.http.get<any>(this.apiUrl + 'avis/stats?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getAvisReplyTemplates(restaurantId: number | null, scope: 'brand' | 'atelier' | 'card'): any {
		let url = this.apiUrl + 'avis-reply-template?scope=' + scope;
		if (restaurantId) {
			url += '&restaurant_id=' + restaurantId;
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createAvisReplyTemplate(data: any): any {
		return this.http.post<any>(this.apiUrl + 'avis-reply-template', data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateAvisReplyTemplate(id: number, data: any): any {
		return this.http.put<any>(this.apiUrl + 'avis-reply-template/' + id, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteAvisReplyTemplate(id: number, restaurantId: number | null): any {
		let url = this.apiUrl + 'avis-reply-template/' + id;
		if (restaurantId) {
			url += '?restaurant_id=' + restaurantId;
		}
		return this.http.delete<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public addFullRestaurant(data: any): any {
		return this.http.post<any>(this.apiUrl + 'full_restaurant', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));

	}

	public deleteFullRestaurant(id: any): any {
		return this.http.delete<any>(this.apiUrl + 'full_restaurant/' + id,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));

	}

	public createComptageCaisse(data: any): any {
		return this.http.post<any>(this.apiUrl + 'caisse/comptage', data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));

	}

	public getPlanningComment(restaurantId: number, week: string): any {
		return this.http.get<any>(this.apiUrl + 'planning_comment?restaurant_id=' + restaurantId + '&week=' + week,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	updatePlanningComment(id: any, data: any) {
		return this.http.put<any>(this.apiUrl + 'planning_comment/' + id,
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createPlanningComment(data: any) {
		return this.http.post<any>(this.apiUrl + 'planning_comment',
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getPlanningTemplateComment(restaurantId: number): any {
		return this.http.get<any>(this.apiUrl + 'planning_template_comment?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	updatePlanningTemplateComment(id: any, data: any) {
		return this.http.put<any>(this.apiUrl + 'planning_template_comment/' + id,
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createPlanningTemplateComment(data: any) {
		return this.http.post<any>(this.apiUrl + 'planning_template_comment',
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public cancelPlanning(id) {
		return this.http.post<any>(this.apiUrl + 'planning/' + id + '/cancel',
			{},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public requestPlanning(id) {
		return this.http.post<any>(this.apiUrl + 'planning/' + id + '/request',
			{},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public connectAs(id) {
		return this.http.post<any>(this.apiUrl + 'auth/connect-as',
			{'id': id},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getClosuresStats(restaurant_id, month?) {
		return this.http.get<any>(this.apiUrl + 'closure?restaurant_id=' + restaurant_id + (month ? '&month=' + month : ''),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCommunicationSent(restaurant_id) {
		return this.http.get<any>(this.apiUrl + 'communication_sent?restaurant_id=' + restaurant_id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getTicketsCategories() {
		return this.http.get<any>(this.apiUrl + 'ticket/categories',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getTicketAssignmentRules() {
		return this.http.get<any>(this.apiUrl + 'ticket/assignment-rules', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createTicketAssignmentRule(data) {
		return this.http.post<any>(this.apiUrl + 'ticket/assignment-rules', data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateTicketAssignmentRule(id: number, data) {
		return this.http.put<any>(this.apiUrl + 'ticket/assignment-rules/' + id, data, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteTicketAssignmentRule(id: number) {
		return this.http.post<any>(this.apiUrl + 'ticket/assignment-rules/' + id + '/delete', {}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getTicketCreationContext(categoryId: number, restaurantId: number) {
		return this.http.get<any>(
			this.apiUrl + 'ticket/creation-context?category_id=' + categoryId + '&restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getRedevanceConfig() {
		return this.http.get<any>(this.apiUrl + 'redevance_config?restaurant_id=',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getTicketsStatuses() {
		return this.http.get<any>(this.apiUrl + 'ticket/status',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getTickets() {
		return this.http.get<any>(this.apiUrl + 'ticket',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createTicket(ticket) {
		return this.http.post<any>(this.apiUrl + 'ticket',
			ticket,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getTicketDetail(ticketId: number) {
		return this.http.get<any>(this.apiUrl + 'ticket/' + ticketId + '/details',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCommandeByToken(token) {
		return this.http.get<any>(this.apiUrl + 'commande?token=' + token,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public addMessageToTicket(id, message?, status?, type?, file_content?): any {
		return this.http.post<any>(this.apiUrl + 'ticket/' + id + '/message' + (status ? '?status=' + status : ''),
			{'message': message, 'file_content': file_content, 'type': type},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public toggleTicketReaction(ticketId: number, messageKey: string, emojiName: string) {
		return this.http.post<{action: 'added' | 'removed'; reactions: any[]}>(
			this.apiUrl + 'ticket/' + ticketId + '/reaction',
			{message_key: messageKey, emoji_name: emojiName},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public validateCommande(commande: any) {
		return this.http.post<any>(this.apiUrl + 'commande',
			{'token': commande.token, 'validate': 1},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public addFournisseurRestaurant(restaurantId: number, data) {

		return this.http.post<any>(this.apiUrl + 'fournisseur_restaurant',
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	addProfilePic(image): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'user_pic',
			image, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	hasUserProfilePic(userId: number): Observable<{has_pic: boolean}> {
		return this.http.get<{has_pic: boolean}>(
			this.apiUrl + 'user_pic?id=' + userId + '&check=1',
		).pipe(catchError(this.handleError));
	}

	public addUserToTicket(id: number, employee_id: any): any {
		return this.http.post<any>(this.apiUrl + 'ticket/' + id + '/add-user',
			{'user_id': employee_id},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public addNotificationToken(currentToken: string, platform = 'web'): any {
		return this.http.post<any>(this.apiUrl + 'user_notification_token',
			{'token': currentToken, 'platform': platform},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public loadInvoice(restaurantId: any, period: string): any {
		return this.http.get<any>(this.apiUrl + 'redevance?restaurant_id=' + restaurantId + '&period=' + period,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisGroupeTemplates(restaurantId: number): any {
		return this.http.get<any>(
			this.apiUrl + 'devis/groupe-templates?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public createDevisGroupeTemplate(restaurantId: number, data: any): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/groupe-templates?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public updateDevisGroupeTemplate(restaurantId: number, templateId: number, data: any): any {
		return this.http.put<any>(
			this.apiUrl + 'devis/groupe-templates/' + templateId + '?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public deleteDevisGroupeTemplate(restaurantId: number, templateId: number): any {
		return this.http.delete<any>(
			this.apiUrl + 'devis/groupe-templates/' + templateId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public applyDevisGroupeTemplate(devisId: number, templateId: number): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/apply-template',
			{template_id: templateId},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisList(restaurantId: any, type: string): any {
		return this.http.get<any>(this.apiUrl + 'devis?restaurant_id=' + restaurantId + '&type=' + type,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevis(id: number): any {
		return this.http.get<any>(this.apiUrl + 'devis/' + id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public generateInvoice(data): any {
		return this.http.post<any>(this.apiUrl + 'redevance', data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisLabel(): any {

		return this.http.get<any>(this.apiUrl + 'devis/label',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisVersions(devisId: number): any {

		return this.http.get<any>(this.apiUrl + 'devis/' + devisId + '/versions',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisItems(devisId: number, versionId: number): any {
		return this.http.get<any>(this.apiUrl + 'devis/' + devisId + '/' + versionId + '/items',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public duplicateVersion(devisId, versionId): any {
		return this.http.post<any>(this.apiUrl + 'devis/' + devisId + '/' + versionId + '/duplicate', {}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateDevis(devisId, data): any {
		return this.http.put<any>(this.apiUrl + 'devis/' + devisId, data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createDevis(data): any {
		return this.http.post<any>(this.apiUrl + 'devis', data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisByReservation(restaurantId: number, reservationId: number): any {
		return this.http.get<any>(
			this.apiUrl + 'devis?restaurant_id=' + restaurantId + '&reservation_id=' + reservationId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public updateDevisItem(devisId, versionId, itemId, data): any {
		return this.http.put<any>(this.apiUrl + 'devis/' + devisId + '/' + versionId + '/' + itemId, data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createDevisItem(devisId, versionId, data): any {
		return this.http.post<any>(this.apiUrl + 'devis/' + devisId + '/' + versionId + '/item', data, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public validateVersion(devisId: number, versionId: number): any {
		return this.http.post<any>(this.apiUrl + 'devis/' + devisId + '/' + versionId + '/validate', {}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public cancelVersion(devisId: number, versionId: number): any {
		return this.http.post<any>(this.apiUrl + 'devis/' + devisId + '/' + versionId + '/cancel', {}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public generateQuote(devisId: number, versionId: number, sendEmail: boolean = true): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/' + versionId + '/generate',
			{send_email: sendEmail ? 1 : 0},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public sendDevisQuote(devisId: number, versionId: number): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/' + versionId + '/send',
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public adjustDevisLines(devisId: number, data: any): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/adjust-lines',
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public generateDevisInvoice(devisId: number, versionId: number): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/' + versionId + '/invoice',
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public remindDevisInvoice(devisId: number): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/invoice-remind',
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public recalculateDevisTva(devisId: number, tvaMode: string): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/recalculate-tva',
			{tva_mode: tvaMode},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisProductionSheet(devisId: number): any {
		return this.http.get<any>(
			this.apiUrl + 'devis/' + devisId + '/production-sheet',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public refreshDevisProductionSheet(devisId: number): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/production-sheet',
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisProductionNeeds(restaurantId: number, weekStart: string): any {
		return this.http.get<any>(
			this.apiUrl + 'devis/production-needs?restaurant_id=' + restaurantId + '&week_start=' + weekStart,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisAvailableItems(restaurantId: number): any {
		return this.http.get<any>(
			this.apiUrl + 'devis/available-items?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisCatalog(restaurantId: number | null, options?: {usage?: string; includeInactive?: boolean}): any {
		const params = [];
		if (restaurantId) {
			params.push('restaurant_id=' + restaurantId);
		}
		if (options?.usage) {
			params.push('usage=' + encodeURIComponent(options.usage));
		}
		if (options?.includeInactive) {
			params.push('include_inactive=1');
		}
		const query = params.length ? ('?' + params.join('&')) : '';
		return this.http.get<any>(
			this.apiUrl + 'devis/catalog' + query,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public createDevisCatalogItem(restaurantId: number | null, data: any): any {
		const query = restaurantId ? ('?restaurant_id=' + restaurantId) : '';
		return this.http.post<any>(
			this.apiUrl + 'devis/catalog' + query,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public updateDevisCatalogItem(restaurantId: number | null, itemId: number, data: any): any {
		const query = restaurantId ? ('?restaurant_id=' + restaurantId) : '';
		return this.http.put<any>(
			this.apiUrl + 'devis/catalog/' + itemId + query,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public saveDevisCatalogPrice(restaurantId: number, itemId: number, price: number | null): any {
		return this.http.put<any>(
			this.apiUrl + 'devis/catalog/' + itemId + '/price?restaurant_id=' + restaurantId,
			{price},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public deleteDevisCatalogItem(restaurantId: number | null, itemId: number): any {
		const query = restaurantId ? ('?restaurant_id=' + restaurantId) : '';
		return this.http.delete<any>(
			this.apiUrl + 'devis/catalog/' + itemId + query,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public saveDevisAvailableItemLinks(itemId: number, links: any[]): any {
		return this.http.put<any>(
			this.apiUrl + 'devis/available-items/' + itemId + '/product-links',
			{links},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public generateVersion(devisId: number): any {
		return this.http.post<any>(this.apiUrl + 'devis/' + devisId + '/generate', {}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public deleteDevisItem(devisId: number, versionId: number, itemId: number): any {
		return this.http.delete<any>(this.apiUrl + 'devis/' + devisId + '/' + versionId + '/' + itemId, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public setTicketDevAuto(ticketId: number, enabled: boolean, agentType?: 'groin' | 'site' | 'new_site') {
		const body: {enabled: boolean; agent_type?: 'groin' | 'site' | 'new_site'} = {enabled};
		if (agentType) {
			body.agent_type = agentType;
		}
		return this.http.post<any>(
			this.apiUrl + 'ticket/' + ticketId + '/dev-auto',
			body,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public updateTicket(ticketId: number, ticket): any {

		return this.http.put<any>(this.apiUrl + 'ticket/' + ticketId,
			ticket,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public answerAvis(avis: any): any {
		return this.http.put<any>(this.apiUrl + 'avis/' + avis.id,
			avis,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public dismissAvis(id: number, restaurantId: number): any {
		return this.http.post<any>(this.apiUrl + 'avis/' + id + '/dismiss',
			{restaurant_id: restaurantId},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getCarteProduitIcons(): any {
		return this.http.get<any>(this.apiUrl + 'carte_produit/icon',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCarteProduitBanners(): any {
		return this.http.get<any>(this.apiUrl + 'carte_produit/banner',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCarteCategorie(): any {
		return this.http.get<any>(this.apiUrl + 'carte_categorie',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCarteProduit(): any {
		return this.http.get<any>(this.apiUrl + 'carte_produit',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createCarteProduit(item: any): any {
		return this.http.post<any>(this.apiUrl + 'carte_produit', item, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateCarteProduit(item: any): any {
		return this.http.put<any>(this.apiUrl + 'carte_produit/' + item.id, item, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public uploadCarteProduitPhoto(productId: number, file: File): any {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<any>(
			this.apiUrl + 'carte_produit/' + productId + '/photo',
			formData,
			this.getMultipartHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public getCarteProduitTranslation(lang: string): any {
		return this.http.get<any>(this.apiUrl + 'carte_produit_translation/' + lang,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createCarteProduitTranslation(item: any): any {
		return this.http.post<any>(this.apiUrl + 'carte_produit_translation', item, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateCarteProduitTranslation(item: any): any {
		return this.http.put<any>(this.apiUrl + 'carte_produit_translation/' + item.id, item, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCarteProduitRestaurant(lang: string): any {
		return this.http.get<any>(this.apiUrl + 'carte_produit_restaurant/' + lang,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createCarteProduitRestaurant(item: any): any {
		return this.http.post<any>(this.apiUrl + 'carte_produit_restaurant', item, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateCarteProduitRestaurant(item: any): any {
		return this.http.put<any>(this.apiUrl + 'carte_produit_restaurant/' + item.id, item, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getWebsiteHomePopup(country: string): any {
		return this.http.get<any>(this.apiUrl + 'website_home_popup/' + encodeURIComponent(country),
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateWebsiteHomePopup(country: string, payload: {is_active: boolean; redirect_path?: string | null; title?: string | null; button_label?: string | null}): any {
		return this.http.put<any>(this.apiUrl + 'website_home_popup/' + encodeURIComponent(country),
			payload,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public uploadWebsiteHomePopupImage(country: string, file: File): any {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<any>(this.apiUrl + 'website_home_popup/' + encodeURIComponent(country) + '/image',
			formData,
			this.getMultipartHttpOptions()).pipe(catchError(this.handleError));
	}

	public listWebsiteHomePopupCampaigns(country?: string): any {
		const path = country
			? 'website_home_popup/campaigns/' + encodeURIComponent(country)
			: 'website_home_popup/campaigns';
		return this.http.get<any>(this.apiUrl + path,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getWebsiteHomePopupCampaign(id: number): any {
		return this.http.get<any>(this.apiUrl + 'website_home_popup/campaign/' + id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createWebsiteHomePopupCampaign(payload: Record<string, unknown>): any {
		return this.http.post<any>(this.apiUrl + 'website_home_popup/campaign',
			payload,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateWebsiteHomePopupCampaign(id: number, payload: Record<string, unknown>): any {
		return this.http.put<any>(this.apiUrl + 'website_home_popup/campaign/' + id,
			payload,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public duplicateWebsiteHomePopupCampaign(id: number): any {
		return this.http.post<any>(this.apiUrl + 'website_home_popup/campaign/' + id + '/duplicate',
			{},
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public deleteWebsiteHomePopupCampaign(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'website_home_popup/campaign/' + id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public translateWebsiteHomePopup(payload: {title: string | null; button_label: string | null}): any {
		return this.http.post<any>(this.apiUrl + 'website_home_popup/translate',
			payload,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public uploadWebsiteHomePopupCampaignImage(id: number, lang: string, file: File): any {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<any>(
			this.apiUrl + 'website_home_popup/campaign/' + id + '/image/' + encodeURIComponent(lang),
			formData,
			this.getMultipartHttpOptions()).pipe(catchError(this.handleError));
	}

	public getWebsiteFranchise(): any {
		return this.http.get<any>(this.apiUrl + 'website_franchise/bundle', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveWebsiteFranchiseCategory(item: {id?: number; name: string; slug?: string; description?: string | null; sort_order?: number}): any {
		const path = item.id ? 'website_franchise/categories/' + item.id : 'website_franchise/categories';
		const request = item.id
			? this.http.put<any>(this.apiUrl + path, item, this.getHttpOptions())
			: this.http.post<any>(this.apiUrl + path, item, this.getHttpOptions());
		return request.pipe(catchError(this.handleError));
	}

	public deleteWebsiteFranchiseCategory(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'website_franchise/categories/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveWebsiteFranchiseArticle(item: Record<string, unknown> & {id?: number}): any {
		const path = item.id ? 'website_franchise/articles/' + item.id : 'website_franchise/articles';
		const request = item.id
			? this.http.put<any>(this.apiUrl + path, item, this.getHttpOptions())
			: this.http.post<any>(this.apiUrl + path, item, this.getHttpOptions());
		return request.pipe(catchError(this.handleError));
	}

	public deleteWebsiteFranchiseArticle(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'website_franchise/articles/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public uploadWebsiteFranchiseCover(id: number, file: File): any {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<any>(this.apiUrl + 'website_franchise/articles/' + id + '/cover', formData, this.getMultipartHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteWebsiteFranchiseCover(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'website_franchise/articles/' + id + '/cover', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveWebsiteFranchiseTestimonial(item: {id?: number; quote: string; author: string; role: string; sort_order?: number; is_published?: boolean}): any {
		const path = item.id ? 'website_franchise/testimonials/' + item.id : 'website_franchise/testimonials';
		const request = item.id
			? this.http.put<any>(this.apiUrl + path, item, this.getHttpOptions())
			: this.http.post<any>(this.apiUrl + path, item, this.getHttpOptions());
		return request.pipe(catchError(this.handleError));
	}

	public deleteWebsiteFranchiseTestimonial(id: number): any {
		return this.http.delete<any>(this.apiUrl + 'website_franchise/testimonials/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveWebsiteFranchiseSettings(item: Record<string, string>): any {
		return this.http.put<any>(this.apiUrl + 'website_franchise/settings', item, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateUserLang(lang) {
		return this.http.post<any>(this.apiUrl + 'user/lang', {'lang': lang},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getUserPreferences(): Observable<Record<string, string>> {
		return this.http.get<Record<string, string>>(this.apiUrl + 'user/preferences', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public setUserPreference(key: string, value: string): Observable<{key: string; value: string}> {
		return this.http.post<{key: string; value: string}>(this.apiUrl + 'user/preferences', {key, value},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public me(): any {
		return this.http.get<any>(this.apiUrl + 'user/me', this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	private boutiqueUrl(path: string, restaurantId: number, query: Record<string, string | number | boolean | null | undefined> = {}): string {
		const params = new URLSearchParams();
		params.set('restaurant_id', String(restaurantId));
		Object.keys(query).forEach(key => {
			const value = query[key];
			if (value !== null && value !== undefined && value !== '') {
				params.set(key, String(value));
			}
		});
		return this.apiUrl + 'boutique' + path + '?' + params.toString();
	}

	public getBoutiqueCatalog(restaurantId: number, all = false, archived = false): Observable<any> {
		return this.http.get<any>(this.boutiqueUrl('/catalog', restaurantId, {
			all: all ? 1 : null,
			archived: archived ? 1 : null,
		}), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getBoutiqueProduct(restaurantId: number, productId: number): Observable<any> {
		return this.http.get<any>(this.boutiqueUrl('/product/' + productId, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveBoutiqueProduct(restaurantId: number, productId: number | null, payload: any): Observable<any> {
		const url = this.boutiqueUrl(productId ? '/product/' + productId : '/product', restaurantId);
		const req = productId
			? this.http.put<any>(url, payload, this.getHttpOptions())
			: this.http.post<any>(url, payload, this.getHttpOptions());
		return req.pipe(catchError(this.handleError));
	}

	public saveBoutiqueVariant(restaurantId: number, productId: number, variantId: number | null, payload: any): Observable<any> {
		const url = variantId
			? this.boutiqueUrl('/product/' + productId + '/variant/' + variantId, restaurantId)
			: this.boutiqueUrl('/product/' + productId + '/variant', restaurantId);
		const req = variantId
			? this.http.put<any>(url, payload, this.getHttpOptions())
			: this.http.post<any>(url, payload, this.getHttpOptions());
		return req.pipe(catchError(this.handleError));
	}

	public deleteBoutiqueVariant(restaurantId: number, variantId: number): Observable<any> {
		return this.http.delete<any>(this.boutiqueUrl('/variant/' + variantId, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public setBoutiqueProductActive(restaurantId: number, productId: number, active: boolean): Observable<any> {
		return this.http.put<any>(this.boutiqueUrl('/product/' + productId + '/active', restaurantId), {active}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public setBoutiqueProductArchived(restaurantId: number, productId: number, archived: boolean): Observable<any> {
		return this.http.put<any>(this.boutiqueUrl('/product/' + productId + '/archive', restaurantId), {archived}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteBoutiqueProduct(restaurantId: number, productId: number): Observable<any> {
		return this.http.delete<any>(this.boutiqueUrl('/product/' + productId, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public uploadBoutiquePhoto(restaurantId: number, kind: 'product' | 'variant', id: number, data: string): Observable<any> {
		const path = kind === 'product' ? '/product/' + id + '/photo' : '/variant/' + id + '/photo';
		return this.http.post<any>(this.boutiqueUrl(path, restaurantId), {data}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getBoutiquePhoto(restaurantId: number, url: string): Observable<Blob> {
		return this.http.get(this.apiUrl.replace(/\/$/, '') + url + '?restaurant_id=' + restaurantId, this.getBlobHttpOptions())
			.pipe(catchError(this.handleError)) as unknown as Observable<Blob>;
	}

	public getBoutiqueRestaurants(restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.boutiqueUrl('/restaurants', restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getBoutiqueReorder(restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.boutiqueUrl('/reorder', restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveBoutiqueSettings(restaurantId: number, payload: any): Observable<any> {
		return this.http.put<any>(this.boutiqueUrl('/settings', restaurantId), payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public createBoutiqueOrder(restaurantId: number, payload: any): Observable<any> {
		return this.http.post<any>(this.boutiqueUrl('/order', restaurantId), payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public listBoutiqueOrders(restaurantId: number, filters: Record<string, string | number | null> = {}): Observable<any[]> {
		return this.http.get<any[]>(this.boutiqueUrl('/order', restaurantId, filters), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getBoutiqueOrder(restaurantId: number, orderId: number): Observable<any> {
		return this.http.get<any>(this.boutiqueUrl('/order/' + orderId, restaurantId), this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public updateBoutiqueOrder(restaurantId: number, orderId: number, payload: any): Observable<any> {
		return this.http.put<any>(this.boutiqueUrl('/order/' + orderId, restaurantId), payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveBoutiqueOrderShipping(restaurantId: number, orderId: number, shippingFee: number | null): Observable<any> {
		return this.http.put<any>(this.boutiqueUrl('/order/' + orderId + '/shipping', restaurantId), {shipping_fee: shippingFee}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getRedevanceShippingFees(restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'redevance/shipping-fees?restaurant_id=' + restaurantId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public payBoutiqueOrder(restaurantId: number, orderId: number, method: 'card' | 'transfer'): Observable<any> {
		return this.http.post<any>(this.boutiqueUrl('/order/' + orderId + '/pay', restaurantId), {method}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public confirmBoutiqueOrder(restaurantId: number, orderId: number): Observable<any> {
		return this.http.post<any>(this.boutiqueUrl('/order/' + orderId + '/confirm', restaurantId), {}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public markBoutiqueOrderPaid(restaurantId: number, orderId: number): Observable<any> {
		return this.http.post<any>(this.boutiqueUrl('/order/' + orderId + '/mark-paid', restaurantId), {}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public setBoutiqueOrderStatus(restaurantId: number, orderId: number, payload: any): Observable<any> {
		return this.http.post<any>(this.boutiqueUrl('/order/' + orderId + '/status', restaurantId), payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public refundBoutiqueOrder(restaurantId: number, orderId: number, lines: Array<{id: number; qty: number}>): Observable<any> {
		return this.http.post<any>(this.boutiqueUrl('/order/' + orderId + '/refund', restaurantId), {lines}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public exportBoutiqueOrders(restaurantId: number, filters: Record<string, string | number | null> = {}): Observable<Blob> {
		return this.http.get(this.boutiqueUrl('/order/export', restaurantId, filters), this.getBlobHttpOptions())
			.pipe(catchError(this.handleError)) as unknown as Observable<Blob>;
	}

	public importBoutiqueCatalog(restaurantId: number): Observable<any> {
		return this.http.post<any>(this.boutiqueUrl('/import', restaurantId), {download_images: true}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	private getHttpOptions(): {headers: HttpHeaders} {
		return {headers: this.buildAuthHeaders()};
	}

	private getMultipartHttpOptions(): {headers: HttpHeaders} {
		return {headers: this.buildAuthHeaders().delete('Content-Type')};
	}

	private getBlobHttpOptions() {
		return {
			headers: this.buildAuthHeaders(),
			responseType: 'blob' as 'json',
		};
	}

	private buildAuthHeaders(): HttpHeaders {
		let headers = this.httpOptions.headers;
		if (this.token) {
			headers = headers.set('Authorization', 'Bearer ' + this.token);
		} else {
			headers = headers.delete('Authorization');
		}
		return headers;
	}

	public getCaMonth(id): any {
		return this.http.get<any>(this.apiUrl + 'stats/ca_month?restaurant_id=' + id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getSupersetToken(id): any {
		return this.http.get<any>(this.apiUrl + 'stats/superset?restaurant_id=' + id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getCaYear(id): any {
		return this.http.get<any>(this.apiUrl + 'stats/ca_year?restaurant_id=' + id,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getResaComments(id: number) {
		return this.http.get<any>(this.apiUrl + 'reservation/' + id + '/comments',
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getResaInfo(restaurantId, telephone: string) {
		return this.http.get<any>(this.apiUrl + 'reservation/info?restaurant_id=' + restaurantId + '&telephone=' + encodeURIComponent(telephone),
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public addResaComment(id: number, comment: any, notify: boolean = true): any {
		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/comment', {
			'content': comment,
			'notify': notify,
		}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getReservationFiles(reservationId: number): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'reservation-file/reservation/' + reservationId, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public uploadReservationFile(payload: any): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'reservation-file', payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public deleteReservationFile(id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'reservation-file/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public downloadReservationFile(id: number): Observable<Blob> {
		return this.http.get<Blob>(this.apiUrl + 'reservation-file/' + id, this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public getTicketFileContent(ticketId: number, slug: string): Observable<Blob> {
		return this.http.get<Blob>(this.apiUrl + 'ticket/' + ticketId + '/' + slug + '/content', this.getBlobHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public loadDevisPaiements(devisId: number): any {
		return this.http.get<any>(this.apiUrl + 'devis/' + devisId + '/paiements',
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updatePaiements(devisId: number): any {
		return this.http.post<any>(this.apiUrl + 'devis/' + devisId + '/update-payments', {}, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public markDevisTransferReceived(devisId: number, type: 'acompte' | 'solde' | 'all' = 'acompte'): any {
		return this.http.post<any>(
			this.apiUrl + 'devis/' + devisId + '/mark-transfer-received',
			{type},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadUpcomingPrestations(restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'devis/upcoming-prestations?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisLiveEventsForDate(restaurantId: number, date: string): Observable<any[]> {
		return this.http.get<any[]>(
			this.apiUrl + 'devis/day-live-events?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisTraiteurTasksForDate(restaurantId: number, date: string): Observable<any[]> {
		return this.http.get<any[]>(
			this.apiUrl + 'devis/day-traiteur-tasks?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisProductsToPrepare(devisId: number, restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(
			this.apiUrl + 'devis/' + devisId + '/products-to-prepare?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public loadDevisLiveProducts(restaurantId: number): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'devis/live-products?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisLiveRestaurants(devisId: number, restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'devis/' + devisId + '/live-restaurants?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateDevisLive(devisId: number, restaurantId: number, data: {live_mode: boolean; live_restaurant_id: number | null}): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'devis/' + devisId + '/live?restaurant_id=' + restaurantId, data,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public loadDevisTodos(devisId: number, restaurantId: number): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'devis/' + devisId + '/todos?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public createDevisTodo(devisId: number, restaurantId: number, data: {
		title: string;
		description?: string | null;
		due_date: string;
		due_time?: string | null;
		duration_minutes?: number | null;
	}): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'devis/' + devisId + '/todo?restaurant_id=' + restaurantId, data,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateDevisTodo(devisId: number, restaurantId: number, todoId: number, data: any): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'devis/' + devisId + '/todo/' + todoId + '?restaurant_id=' + restaurantId, data,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public updateDevisProductPreparation(devisId: number, restaurantId: number, data: {
		link_type: string;
		product_id: number;
		component_product_id?: number;
		scheduled_date?: string | null;
		scheduled_time?: string | null;
		duration_minutes?: number | null;
		completed?: boolean;
		completion_comment?: string | null;
	}): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'devis/' + devisId + '/product-preparation?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public deleteDevisTodo(devisId: number, restaurantId: number, todoId: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'devis/' + devisId + '/todo/' + todoId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public saveDevisItemLinks(devisId: number, versionId: number, itemId: number, restaurantId: number, links: any[]): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'devis/' + devisId + '/' + versionId + '/' + itemId + '/links?restaurant_id=' + restaurantId,
			{links},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	public deleteUserFromTicket(id: number, employee_id: any): any {
		return this.http.post<any>(this.apiUrl + 'ticket/' + id + '/delete-user',
			{'user_id': employee_id},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	public saveCommandeLocal(restaurant_id, item: any): any {
		return this.http.post<any>(this.apiUrl + 'commande_local?restaurant_id=' + restaurant_id, item, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public deleteNotification(id): any {
		return this.http.delete<any>(this.apiUrl + 'notification?id=' + id, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public getNotifications(): any {
		return this.http.get<any>(this.apiUrl + 'notification', this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getClosing(restaurantId: number, period: string): any {
		return this.http.get<any>(this.apiUrl + 'closing/' + period + '?restaurant_id=' + restaurantId,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	updateClosing(id: any, data: any, restaurantId?: number) {
		const query = restaurantId ? '?restaurant_id=' + restaurantId : '';
		return this.http.put<any>(this.apiUrl + 'closing/' + id + query,
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createClosing(data: any, restaurantId?: number) {
		const query = restaurantId ? '?restaurant_id=' + restaurantId : '';
		return this.http.post<any>(this.apiUrl + 'closing' + query,
			data,
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}


	public getStat(restaurantId, category: string, stat: string, force: boolean = false, month: string = null): any {
		let url = this.apiUrl + 'stats/' + category + '/' + stat + '?restaurant_id=' + restaurantId + '&force=' + force.toString();
		if (month) {
			url += '&month=' + month;
		}
		return this.http.get<any>(url,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}


	public getNetworkStats(month = null): any {
		let url = this.apiUrl + 'stats/network';
		if (month) {
			url += '?month=' + month;
		}
		return this.http.get<any>(url,
			this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	public ignoreResa(id, ignoreDate: any): any {

		return this.http.post<any>(this.apiUrl + 'reservation/' + id + '/ignore',
			{ignore_until: ignoreDate},
			this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getKlipyTrending(page = 1, perPage = 20): Observable<any> {
		const appKey = 'Dw6KGiy7Vzxir69vLeT6MBu51r98i97je6uzSpvKMiYgu7W3szMX7NAKfN1PcjAM';
		const url = `https://api.klipy.com/api/v1/${appKey}/gifs/trending?page=${page}&per_page=${perPage}`;
		return this.http.get<any>(url).pipe(catchError(this.handleError));
	}

	getKlipySearch(q: string, page = 1, perPage = 20): Observable<any> {
		const appKey = 'Dw6KGiy7Vzxir69vLeT6MBu51r98i97je6uzSpvKMiYgu7W3szMX7NAKfN1PcjAM';
		const url = `https://api.klipy.com/api/v1/${appKey}/gifs/search?page=${page}&per_page=${perPage}&q=${q}`;
		return this.http.get<any>(url).pipe(catchError(this.handleError));
	}

	getEquipements(restaurantId: number) {
		return this.http.get<AtelierEquipementListItem[]>(
			this.apiUrl + 'equipement?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipement(restaurantId: number, id: number) {
		return this.http.get<AtelierEquipementDetail>(
			this.apiUrl + 'equipement/' + id + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createEquipement(restaurantId: number, data: Record<string, unknown>) {
		return this.http.post<AtelierEquipementDetail>(
			this.apiUrl + 'equipement?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipement(restaurantId: number, id: number, data: Record<string, unknown>) {
		return this.http.put<AtelierEquipementDetail>(
			this.apiUrl + 'equipement/' + id + '?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	setEquipementRebus(restaurantId: number, id: number, rebusReason: string) {
		return this.http.post<AtelierEquipementDetail>(
			this.apiUrl + 'equipement/' + id + '/rebus?restaurant_id=' + restaurantId,
			{ rebus_reason: rebusReason },
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	clearEquipementRebus(restaurantId: number, id: number) {
		return this.http.post<AtelierEquipementDetail>(
			this.apiUrl + 'equipement/' + id + '/rebus?restaurant_id=' + restaurantId,
			{ clear: true },
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipement(restaurantId: number, id: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement/' + id + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	uploadEquipementFacture(restaurantId: number, equipementId: number, data: string, name: string) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/' + equipementId + '/facture?restaurant_id=' + restaurantId,
			{ data, name },
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementFacture(restaurantId: number, equipementId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement/' + equipementId + '/facture?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	downloadEquipementFacture(restaurantId: number, equipementId: number) {
		return this.http.get<any>(
			this.apiUrl + 'equipement/' + equipementId + '/facture?restaurant_id=' + restaurantId,
			this.getBlobHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	downloadEquipementDocumentationFile(restaurantId: number, equipementId: number, docId: number) {
		return this.http.get<any>(
			this.apiUrl + 'equipement/' + equipementId + '/documentation/' + docId + '/file?restaurant_id=' + restaurantId,
			this.getBlobHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	downloadEquipementEntretienDocument(restaurantId: number, equipementId: number, entretienId: number, file: string) {
		return this.http.get<any>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien/' + entretienId + '/document?restaurant_id=' + restaurantId
				+ '&file=' + encodeURIComponent(file),
			this.getBlobHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementFactureUrl(restaurantId: number, equipementId: number): string {
		return this.apiUrl + 'equipement/' + equipementId + '/facture?restaurant_id=' + restaurantId;
	}

	addEquipementDocumentation(restaurantId: number, equipementId: number, payload: Record<string, unknown>) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/' + equipementId + '/documentation?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipementDocumentation(restaurantId: number, equipementId: number, docId: number, payload: Record<string, unknown>) {
		return this.http.put<any>(
			this.apiUrl + 'equipement/' + equipementId + '/documentation/' + docId + '?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	refreshEquipementDocumentation(restaurantId: number, equipementId: number, docId: number) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/' + equipementId + '/documentation/' + docId + '/refresh?restaurant_id=' + restaurantId,
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementDocumentation(restaurantId: number, equipementId: number, docId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement/' + equipementId + '/documentation/' + docId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementDocumentationFileUrl(restaurantId: number, equipementId: number, docId: number): string {
		return this.apiUrl + 'equipement/' + equipementId + '/documentation/' + docId + '/file?restaurant_id=' + restaurantId;
	}

	addEquipementEntretien(restaurantId: number, equipementId: number, payload: Record<string, unknown>) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipementEntretien(restaurantId: number, equipementId: number, entretienId: number, payload: Record<string, unknown>) {
		return this.http.put<any>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien/' + entretienId + '?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementEntretien(restaurantId: number, equipementId: number, entretienId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien/' + entretienId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	uploadEquipementEntretienDocument(restaurantId: number, equipementId: number, entretienId: number, data: string, name: string) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien/' + entretienId + '/document?restaurant_id=' + restaurantId,
			{ data, name },
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementEntretienDocumentUrl(restaurantId: number, equipementId: number, entretienId: number, file: string): string {
		return this.apiUrl + 'equipement/' + equipementId + '/entretien/' + entretienId + '/document?restaurant_id=' + restaurantId
			+ '&file=' + encodeURIComponent(file);
	}

	sendEquipementSav(restaurantId: number, equipementId: number, payload: Record<string, unknown>) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/' + equipementId + '/sav?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getSavFournisseurByFournisseurId(fournisseurId: number, restaurantId: number, includeInactive = false) {
		let url = this.apiUrl + 'sav-fournisseur?fournisseur_id=' + fournisseurId
			+ '&restaurant_id=' + restaurantId;
		if (includeInactive) {
			url += '&include_inactive=1';
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getSavFournisseurItems(savFournisseurId: number, restaurantId: number, includeInactive = false) {
		let url = this.apiUrl + 'sav-fournisseur/' + savFournisseurId + '/items?restaurant_id=' + restaurantId;
		if (includeInactive) {
			url += '&include_inactive=1';
		}
		return this.http.get<any>(url, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createSavFournisseur(restaurantId: number, payload: { fournisseur_id: number; name: string; dest_email: string; active: number }) {
		return this.http.post<any>(
			this.apiUrl + 'sav-fournisseur?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateSavFournisseur(savFournisseurId: number, restaurantId: number, payload: { dest_email: string; active: number }) {
		return this.http.put<any>(
			this.apiUrl + 'sav-fournisseur/' + savFournisseurId + '?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createSavFournisseurItem(savFournisseurId: number, restaurantId: number, payload: { code: string; label: string; active: number; sort_order: number }) {
		return this.http.post<any>(
			this.apiUrl + 'sav-fournisseur/' + savFournisseurId + '/items?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateSavFournisseurItem(savFournisseurId: number, itemId: number, restaurantId: number, payload: { code: string; label: string; active: number }) {
		return this.http.put<any>(
			this.apiUrl + 'sav-fournisseur/' + savFournisseurId + '/items/' + itemId + '?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteSavFournisseurItem(savFournisseurId: number, itemId: number, restaurantId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'sav-fournisseur/' + savFournisseurId + '/items/' + itemId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	sendSavFournisseurRequest(savFournisseurId: number, restaurantId: number, payload: { item_id: number; comment?: string }) {
		return this.http.post<any>(
			this.apiUrl + 'sav-fournisseur/' + savFournisseurId + '/request?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	promoteEquipementToCatalog(restaurantId: number, equipementId: number, payload: Record<string, unknown>) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/' + equipementId + '/promote-catalog?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementCatalogItems(suiviOnly?: boolean, localAddOnly?: boolean, restaurantId?: number) {
		const params: string[] = [];
		if (suiviOnly) {
			params.push('suivi_only=1');
		}
		if (localAddOnly) {
			params.push('local_add_only=1');
		}
		if (restaurantId) {
			params.push('restaurant_id=' + restaurantId);
		}
		let url = this.apiUrl + 'equipement-catalog';
		if (params.length) {
			url += '?' + params.join('&');
		}
		return this.http.get<AtelierEquipementCatalogListItem[]>(
			url,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementCatalogCategories() {
		return this.http.get<AtelierEquipementCatalogCategory[]>(
			this.apiUrl + 'equipement-catalog/categories',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getMffCatalog(restaurantId?: number) {
		let url = this.apiUrl + 'equipement-catalog/mff';
		if (restaurantId) {
			url += '?restaurant_id=' + restaurantId;
		}
		return this.http.get<{ categories: MffCatalogGroup[] }>(
			url,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementCatalogItem(id: number) {
		return this.http.get<AtelierEquipementCatalogDetail>(
			this.apiUrl + 'equipement-catalog/' + id,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createEquipementCatalogItem(data: Record<string, unknown>) {
		return this.http.post<AtelierEquipementCatalogDetail>(
			this.apiUrl + 'equipement-catalog',
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipementCatalogItem(id: number, data: Record<string, unknown>) {
		return this.http.put<AtelierEquipementCatalogDetail>(
			this.apiUrl + 'equipement-catalog/' + id,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementCatalogItem(id: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement-catalog/' + id,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	addEquipementCatalogDocumentation(catalogId: number, payload: Record<string, unknown>) {
		return this.http.post<any>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/documentation',
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipementCatalogDocumentation(catalogId: number, docId: number, payload: Record<string, unknown>) {
		return this.http.put<any>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/documentation/' + docId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementCatalogDocumentation(catalogId: number, docId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/documentation/' + docId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	refreshEquipementCatalogDocumentation(catalogId: number, docId: number) {
		return this.http.post<any>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/documentation/' + docId + '/refresh',
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	downloadEquipementCatalogDocumentationFile(catalogId: number, docId: number) {
		return this.http.get<any>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/documentation/' + docId + '/file',
			this.getBlobHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementCatalogDocumentationFileUrl(catalogId: number, docId: number): string {
		return this.apiUrl + 'equipement-catalog/' + catalogId + '/documentation/' + docId + '/file';
	}

	createEquipementCatalogTroubleshooting(catalogId: number, payload: Record<string, unknown>) {
		return this.http.post<AtelierEquipementTroubleshootingEntry>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/troubleshooting',
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipementCatalogTroubleshooting(catalogId: number, entryId: number, payload: Record<string, unknown>) {
		return this.http.put<AtelierEquipementTroubleshootingEntry>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/troubleshooting/' + entryId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementCatalogTroubleshooting(catalogId: number, entryId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/troubleshooting/' + entryId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementTroubleshooting(restaurantId: number, equipementId: number) {
		return this.http.get<AtelierEquipementTroubleshootingEntry[]>(
			this.apiUrl + 'equipement/' + equipementId + '/troubleshooting?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementEntretienPlanDue(restaurantId: number, date: string) {
		return this.http.get<AtelierEquipementEntretienDueItem[]>(
			this.apiUrl + 'equipement/entretien-plan/due?restaurant_id=' + restaurantId + '&date=' + encodeURIComponent(date),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementEntretienPlanRegister(restaurantId: number, from: string, to: string) {
		return this.http.get<{ days: Array<{ date: string; opened: boolean; entries: any[] }> }>(
			this.apiUrl + 'equipement/entretien-plan/register?restaurant_id=' + restaurantId
				+ '&from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to),
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	completeEquipementEntretienPlan(restaurantId: number, planId: number, payload: { date: string; service: string; moment: string }) {
		return this.http.post<any>(
			this.apiUrl + 'equipement/entretien-plan/' + planId + '/complete?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getEquipementEntretienPlans(restaurantId: number, equipementId: number) {
		return this.http.get<AtelierEquipementEntretienPlan[]>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien-plan?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createEquipementEntretienPlan(restaurantId: number, equipementId: number, payload: Record<string, unknown>) {
		return this.http.post<AtelierEquipementEntretienPlan>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien-plan?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipementEntretienPlan(restaurantId: number, equipementId: number, planId: number, payload: Record<string, unknown>) {
		return this.http.put<AtelierEquipementEntretienPlan>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien-plan/' + planId + '?restaurant_id=' + restaurantId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementEntretienPlan(restaurantId: number, equipementId: number, planId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement/' + equipementId + '/entretien-plan/' + planId + '?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createEquipementCatalogEntretienPlanTemplate(catalogId: number, payload: Record<string, unknown>) {
		return this.http.post<AtelierEquipementEntretienPlan>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/entretien-plan-templates',
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateEquipementCatalogEntretienPlanTemplate(catalogId: number, templateId: number, payload: Record<string, unknown>) {
		return this.http.put<AtelierEquipementEntretienPlan>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/entretien-plan-templates/' + templateId,
			payload,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	deleteEquipementCatalogEntretienPlanTemplate(catalogId: number, templateId: number) {
		return this.http.delete<any>(
			this.apiUrl + 'equipement-catalog/' + catalogId + '/entretien-plan-templates/' + templateId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getReservationPopupEvents(restaurantId: number): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'reservation_event?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getReservationPopupQueue(): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'reservation_event/queue',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getReservationPopupNationalEvents(): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'reservation_event/national',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createReservationPopupRequest(restaurantId: number, data: Record<string, unknown>): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'reservation_event?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createReservationPopupNational(data: Record<string, unknown>): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'reservation_event/national',
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	cancelReservationPopupEvent(eventId: number, restaurantId: number): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/cancel?restaurant_id=' + restaurantId,
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	approveReservationPopupEvent(eventId: number, data: Record<string, unknown>): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/approve',
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	rejectReservationPopupEvent(eventId: number, reviewComment: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/reject',
			{review_comment: reviewComment},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getReservationPopupActiveEvents(restaurantId: number): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'reservation_event/active?restaurant_id=' + restaurantId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	createReservationPopupLocal(data: Record<string, unknown>, restaurantId: number): Observable<any> {
		return this.http.post<any>(
			this.apiUrl + 'reservation_event/local?restaurant_id=' + restaurantId,
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	stopReservationPopupEvent(eventId: number, restaurantId: number): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/stop?restaurant_id=' + restaurantId,
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	resumeReservationPopupEvent(eventId: number): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/resume',
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	requestReservationPopupModification(eventId: number, restaurantId: number, message: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/request-modification?restaurant_id=' + restaurantId,
			{message},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getReservationPopupModificationQueue(): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'reservation_event/modification-queue',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	approveReservationPopupModificationRequest(requestId: number, reviewComment?: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/modification-request/' + requestId + '/approve',
			reviewComment ? {review_comment: reviewComment} : {},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	rejectReservationPopupModificationRequest(requestId: number, reviewComment: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/modification-request/' + requestId + '/reject',
			{review_comment: reviewComment},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getReservationPopupExclusionQueue(): Observable<any> {
		return this.http.get<any>(
			this.apiUrl + 'reservation_event/exclusion-queue',
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	approveReservationPopupExclusionRequest(requestId: number, reviewComment?: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/exclusion-request/' + requestId + '/approve',
			{review_comment: reviewComment || ''},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	rejectReservationPopupExclusionRequest(requestId: number, reviewComment: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/exclusion-request/' + requestId + '/reject',
			{review_comment: reviewComment},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	detachRestaurantFromReservationPopup(eventId: number, restaurantId: number): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/detach-restaurant?restaurant_id=' + restaurantId,
			{},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	shortenReservationPopupSchedule(eventId: number, restaurantId: number, endDate: string): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/shorten-schedule?restaurant_id=' + restaurantId,
			{end_date: endDate},
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateReservationPopupEvent(eventId: number, data: Record<string, unknown>): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/update',
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	updateReservationPopupNational(eventId: number, data: Record<string, unknown>): Observable<any> {
		return this.http.put<any>(
			this.apiUrl + 'reservation_event/' + eventId + '/national',
			data,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getMailTemplates(): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'mail-template', this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	getMailTemplate(key: string): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'mail-template/' + encodeURIComponent(key), this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createMailTemplate(payload: {key: string; label: string; description?: string}): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'mail-template', payload, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	updateMailTemplate(key: string, payload: {label: string; description?: string; mailjet_template_id?: string; use_groin?: boolean}): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'mail-template/' + encodeURIComponent(key), payload, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	saveMailTemplateContent(key: string, payload: {sections: unknown[]; phrases: Record<string, Record<string, string>>}): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'mail-template/' + encodeURIComponent(key) + '/content', payload, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	sendMailTemplateTest(key: string, payload: {lang: string; variables: Record<string, string>}): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'mail-template/' + encodeURIComponent(key) + '/test', payload, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	createMailTemplateVariable(key: string, payload: {name: string; label: string; sample?: string; is_html: boolean}): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'mail-template/' + encodeURIComponent(key) + '/variable', payload, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	updateMailTemplateVariable(key: string, id: number, payload: {name: string; label: string; sample?: string; is_html: boolean}): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'mail-template/' + encodeURIComponent(key) + '/variable/' + id, payload, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	deleteMailTemplateVariable(key: string, id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'mail-template/' + encodeURIComponent(key) + '/variable/' + id, this.getHttpOptions()).pipe(catchError(this.handleError));
	}

	deleteReservationPopupEvent(eventId: number): Observable<any> {
		return this.http.delete<any>(
			this.apiUrl + 'reservation_event/' + eventId,
			this.getHttpOptions(),
		).pipe(catchError(this.handleError));
	}

	getActiveUiTheme(): Observable<{theme: any | null}> {
		return this.http.get<{theme: any | null}>(this.apiUrl + 'ui-theme/active', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getUiThemes(): Observable<any[]> {
		return this.http.get<any[]>(this.apiUrl + 'ui-theme', this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createUiTheme(payload: {name: string; background_light: string; tint_light: string; background_dark: string; tint_dark: string; loader_light: string; loader_dark: string; mark: string; animated: number}): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'ui-theme', payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateUiTheme(id: number, payload: {name: string; background_light: string; tint_light: string; background_dark: string; tint_dark: string; loader_light: string; loader_dark: string; mark: string; animated: number}): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'ui-theme/' + id, payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	deleteUiTheme(id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'ui-theme/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createUiThemePeriod(themeId: number, payload: {starts_on: string; ends_on: string; repeats_yearly: number; priority: number}): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'ui-theme/' + themeId + '/period', payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateUiThemePeriod(id: number, payload: {starts_on: string; ends_on: string; repeats_yearly: number; priority: number}): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'ui-theme/period/' + id, payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	deleteUiThemePeriod(id: number): Observable<any> {
		return this.http.delete<any>(this.apiUrl + 'ui-theme/period/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getRecrutementOffres(restaurantId: number | null): Observable<any> {
		let query = '';
		if (restaurantId === -1) {
			query = '?siege=1';
		} else if (restaurantId) {
			query = '?restaurant_id=' + restaurantId;
		}
		return this.http.get<any>(this.apiUrl + 'recrutement/offres' + query, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	getRecrutementOffre(id: number): Observable<any> {
		return this.http.get<any>(this.apiUrl + 'recrutement/offres/' + id, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	createRecrutementOffre(payload: {
		restaurant_id?: number;
		siege?: boolean;
		country?: string;
		nom: string;
		description: string;
		experience: string;
		personnalite: string;
		start_date: string;
		type: string;
		salaire: string;
	}): Observable<any> {
		return this.http.post<any>(this.apiUrl + 'recrutement/offres', payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateRecrutementOffre(id: number, payload: {
		restaurant_id?: number;
		siege?: boolean;
		country?: string;
		nom: string;
		description: string;
		experience: string;
		personnalite: string;
		start_date: string;
		type: string;
		salaire: string;
	}): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'recrutement/offres/' + id, payload, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	updateRecrutementOffreStatut(id: number, statut: string): Observable<any> {
		return this.http.put<any>(this.apiUrl + 'recrutement/offres/' + id + '/statut', {statut}, this.getHttpOptions())
			.pipe(catchError(this.handleError));
	}

	downloadRecrutementCv(id: number): Observable<Blob> {
		return this.http.get(this.apiUrl + 'recrutement/candidatures/' + id + '/cv', this.getBlobHttpOptions())
			.pipe(catchError(this.handleError)) as unknown as Observable<Blob>;
	}
}
